import { Fragment, useCallback, useEffect, useRef, useState } from 'react';
import { useBlocker } from 'react-router-dom';
import { apiRequest, getAccessToken, isTokenExpired, refreshAccessToken } from '../utils/auth';
import { useEscapeToClose } from '../utils/useEscapeToClose';
import { hasRichText, parseRuns, richTextError, visibleLength } from '../utils/richText';
import RichTextField from '../components/RichTextField';
import LensPickerModal from '../components/LensPickerModal';
import {
  FiAlertTriangle, FiBell, FiBook, FiCheckCircle, FiChevronDown, FiChevronUp, FiClock, FiColumns, FiCommand, FiEyeOff,
  FiGrid, FiImage, FiLayout, FiMove, FiPlus, FiSave, FiStar, FiTrash2, FiTrendingUp, FiUsers, FiVideo, FiX, FiZap,
} from 'react-icons/fi';
import './HomeLayoutPage.css';

/**
 * Admin editor for the mobile home screen. Multiple layouts render as a
 * horizontal row of phone-frame previews; exactly one can be Active — that's
 * the one the app serves (none active = the app's built-in default).
 * Element types are DRAGGED from the left list onto a phone (drop opens the
 * config modal; only Confirm inserts). Clicking an existing element opens the
 * same modal for editing. Saved wholesale via PUT /api/admin/home-layout.
 */

type ElementType = 'article' | 'banner' | 'quickActions' | 'carousel' | 'recentFraw' | 'topExtensions' | 'curatedExtensions' | 'extensionGrid';
/**
 * `subscription` / `topup` are the app's native store-billed screens; the
 * `web*` pair opens the user portal's own pages in the in-app browser. Both
 * pairs exist on purpose — they bill through different channels.
 */
type BannerActionType = 'blend' | 'rebuild' | 'upscale' | 'subscription' | 'topup' | 'webSubscription' | 'webTopup' | 'extensionList' | 'extension' | 'creator' | 'article' | 'referral' | 'url';
type LoginState = 'all' | 'guest' | 'loggedIn';
type PlatformId = 'ios' | 'android';

interface BannerAction {
  type: BannerActionType;
  categoryId?: string;
  extensionId?: string;
  userId?: string;
  /** Article to open — `article` action only. */
  articleId?: string;
  /** Website opened in the phone's browser — `url` action only. */
  url?: string;
  /** Display name of the extension/creator/article target — resolved by the backend, never persisted. */
  targetName?: string;
}

interface TargetSearchResult {
  id: string;
  name: string;
  sub?: string;
}

/** What sits behind a banner's text. Absent on a banner saved before the choice existed. */
type BannerBackdrop = 'none' | 'gradient' | 'blur';

interface BannerConfig {
  imageKey?: string;
  imageUrl?: string;
  videoKey?: string;
  videoUrl?: string;
  thumbnailKey?: string;
  thumbnailUrl?: string;
  /** Overlay title shown on the card, localized per app language. */
  title?: LocalizedLabel;
  aspectRatio?: number;
  /** Video banners: set by the upload probe, and only then. The app hides its mute
   *  control when it is false, and keeps showing it when the field is absent. */
  hasAudio?: boolean;
  /** Supporting line above the title, the way the intro card carries one. */
  subtitle?: LocalizedLabel;
  /**
   * What is drawn behind the text so it reads over the artwork. Absent = `gradient`,
   * which is what every banner saved before the choice existed carries.
   */
  backdrop?: BannerBackdrop;
  /**
   * `#rrggbb` laid under the frosted band — `blur` only, though it is kept whatever
   * backdrop is selected so switching away and back does not lose it. The app draws
   * it at a fixed half opacity of its own, so it tones that strip of artwork rather
   * than covering it. Absent = untinted.
   */
  backdropColor?: string;
  /**
   * Call-to-action label. Setting it puts a button in the card's bottom corner AND
   * takes the tap off the card itself — from then on the button is the only thing
   * that runs the action.
   */
  buttonLabel?: LocalizedLabel;
  action: BannerAction;
}

/**
 * Localized tile label keyed by app language code. English is required; the
 * app falls back to it when a translation is missing.
 */
interface LocalizedLabel {
  en: string;
  [languageCode: string]: string | undefined;
}

/**
 * One tile in a quickActions or carousel row: image + short label + tap
 * action. quickActions renders square tiles; carousel renders wide cards.
 */
interface QuickAction {
  imageKey?: string;
  imageUrl?: string;
  /**
   * Required on a quickActions tile, where it is a short plain caption.
   * Optional on a carousel item, which may be an uncaptioned card — and there
   * it is one free-form line of rich text the admin styles itself.
   */
  label?: LocalizedLabel;
  description?: LocalizedLabel;
  action: BannerAction;
}

/**
 * One picked lens of a curatedExtensions element. Only `id` is saved; the name
 * and `listed` are display extras from the backend (or the search result).
 */
interface CuratedExtension {
  id: string;
  name: string;
  /** Thumbnail of the lens's first sample output, when it has one. */
  imageUrl?: string;
  /**
   * False when the lens's own state keeps it out of the app (private,
   * unpublished, archived, deleted, suspended). Its creator's plan isn't
   * checked, so true is not a promise. Absent on a lens just picked from search.
   */
  listed?: boolean;
}

/** Most lenses a curatedExtensions element may hold — the backend's cap. */
const MAX_CURATED_EXTENSIONS = 50;

/** The element fields that hold a QuickAction tile list. */
type TilesField = 'quickActions' | 'carouselItems';

interface Visibility {
  enabled: boolean;
  loginState: LoginState;
  plans: string[];
  platforms: PlatformId[];
}

interface LayoutElement {
  elementId: string;
  type: ElementType;
  articleId?: string;
  banner?: BannerConfig;
  quickActions?: QuickAction[];
  carouselItems?: QuickAction[];
  categoryId?: string;
  /**
   * Optional localized section title, available on every element type.
   * Empty = the element's default (topExtensions computes one, recentFraw has
   * a built-in one, other types — including extensionGrid — show none).
   * quickActions renders it inline beside its tiles, not as a header above.
   */
  title?: LocalizedLabel;
  /**
   * quickActions only: optional localized supporting text under the inline
   * title. Either it or `title` switches the row to the titled layout.
   */
  description?: LocalizedLabel;
  limit?: number | null;
  visibility: Visibility;
  /** topExtensions / curatedExtensions: horizontal ranked strip or vertical ranking list. */
  displayStyle?: 'horizontal' | 'vertical';
  /** topExtensions / curatedExtensions: render the rank numbers (default true). */
  showRank?: boolean;
  /** curatedExtensions: the picked lenses in display order. Saved as `extensionIds`. */
  extensions?: CuratedExtension[];
  /**
   * carousel: lay two items out the way three or more are laid out (wide,
   * looping cards) instead of splitting the row evenly. No effect elsewhere.
   */
  wideMode?: boolean;
  /**
   * carousel: whether a scrolling carousel wraps around forever (default true).
   * Off, it stops at the first and last card. Only applies once the cards
   * scroll: three or more items, or two in wide mode.
   */
  infiniteScroll?: boolean;
  /** Admin display extras resolved by the backend */
  article?: {
    articleId: string;
    title: string;
    isPublished?: boolean;
    coverImageUrl?: string;
    coverThumbnailUrl?: string;
  };
}

/** A user this layout is served to instead of the active one. */
interface AssignedUser {
  userId: string;
  name: string;
}

interface AdminLayout {
  layoutId: string;
  name: string;
  isActive: boolean;
  /**
   * Users served THIS layout instead of the active one. Usually set on a layout
   * that is not active — that is the point of it. The backend resolves the
   * names into `assignedUsers` for display; `assignedUserIds` is what it stores.
   */
  assignedUserIds: string[];
  assignedUsers?: AssignedUser[];
  elements: LayoutElement[];
}

interface EditingState {
  mode: 'add' | 'edit';
  /** The layout the draft belongs to. */
  layoutId: string;
  draft: LayoutElement;
  /** add mode: position to insert at when confirmed (default: end). */
  insertAt?: number;
}

type DragState =
  | { kind: 'new'; typeKey: string }
  | { kind: 'reorder'; layoutId: string; index: number };

/** Which localized text the translations modal is editing. */
type LabelEditorTarget =
  | { kind: 'tileLabel'; field: TilesField; index: number }
  | { kind: 'elementTitle' }
  | { kind: 'elementDescription' }
  | { kind: 'bannerTitle' }
  | { kind: 'bannerSubtitle' }
  | { kind: 'bannerButton' };

/** The target's current text in the draft — snapshotted so Cancel can restore it. */
const readLabelEditorValue = (target: LabelEditorTarget, draft: LayoutElement): LocalizedLabel | undefined => {
  switch (target.kind) {
    case 'tileLabel':
      return (draft[target.field] || [])[target.index]?.label;
    case 'elementTitle':
      return draft.title;
    case 'elementDescription':
      return draft.description;
    case 'bannerTitle':
      return draft.banner?.title;
    case 'bannerSubtitle':
      return draft.banner?.subtitle;
    case 'bannerButton':
      return draft.banner?.buttonLabel;
    default:
      return undefined;
  }
};

interface ArticleOption {
  articleId: string;
  title: string;
  coverImageUrl?: string;
}

interface CategoryOption {
  _id: string;
  category_id: string;
  label: { en: string; [code: string]: string | undefined };
}

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3000';

const PLANS = ['free', 'basic', 'pro', 'premium'];

const MAX_QUICK_ACTIONS = 8;
const MAX_QUICK_ACTION_LABEL = 10;
/** A carousel card's caption: one free-form line, so as long as the label + description it replaced. */
const MAX_CAROUSEL_CAPTION = 50;
const MAX_TITLE_LENGTH = 60;
/** A banner's supporting line: one line above the title, sharing the card's bottom row with the button. */
const MAX_BANNER_SUBTITLE = 60;
/** A banner's call-to-action — a pill beside the text, so a couple of words. */
const MAX_BANNER_BUTTON_LABEL = 20;
/** quickActions supporting text — wraps onto several lines beside the tiles. */
const MAX_ELEMENT_DESCRIPTION = 120;

/**
 * Swatches offered in the section-title toolbar. The first is the app's own
 * highlight color (AppTheme text.textHighlight) — the rest are accents that
 * hold up on the app's dark background. Any other color is reachable through
 * the custom picker, so this list is a shortcut, not a restriction.
 */
const TITLE_COLORS = ['#00ffff', '#ffd166', '#ff6b6b', '#7cf29c', '#c792ea'];

/** Renders section-title markup the way the app draws it — plain text stays plain. */
const renderRichText = (text: string) =>
  hasRichText(text)
    ? parseRuns(text).map((run, i) => (
        <span
          key={i}
          style={{
            ...(run.color && { color: run.color }),
            ...(run.bold && { fontWeight: 900 }),
            ...(run.italic && { fontStyle: 'italic' as const }),
            ...(run.underline && { textDecoration: 'underline' }),
          }}
        >
          {run.text}
        </span>
      ))
    : text;

/** The app's supported languages a tile label can be translated into. */
const LABEL_LANGUAGES: { code: string; name: string; required?: boolean }[] = [
  { code: 'en', name: 'English', required: true },
  { code: 'zh-hk', name: 'Traditional Chinese (Hong Kong)' },
  { code: 'zh-tw', name: 'Traditional Chinese (Taiwan)' },
  { code: 'zh-cn', name: 'Simplified Chinese' },
  { code: 'ja', name: 'Japanese' },
  { code: 'ko', name: 'Korean' },
];

/** Legacy tiles (pre-i18n) stored the label as a plain string — read it as English. */
const normalizeTileLabel = (label: LocalizedLabel | string | undefined): LocalizedLabel =>
  typeof label === 'string' ? { en: label } : (label || { en: '' });

/** Trims every translation and drops empty non-English ones for the save payload. */
const cleanTileLabel = (label?: LocalizedLabel): LocalizedLabel => {
  const out: LocalizedLabel = { en: (label?.en || '').trim() };
  for (const lang of LABEL_LANGUAGES) {
    if (lang.code === 'en') continue;
    const value = (label?.[lang.code] || '').trim();
    if (value) out[lang.code] = value;
  }
  return out;
};

/** Like cleanTileLabel, but a fully empty OPTIONAL text becomes undefined (= no title). */
const cleanOptionalText = (label?: LocalizedLabel): LocalizedLabel | undefined => {
  if (!label) return undefined;
  const out = cleanTileLabel(label);
  return out.en || Object.keys(out).length > 1 ? out : undefined;
};

/** Optional localized texts may be empty, but any translation without English is an error. */
const localizedTitleError = (map: LocalizedLabel | undefined, what: string): string | null => {
  if (!map) return null;
  const hasAny = LABEL_LANGUAGES.some((lang) => (map[lang.code] || '').trim());
  if (hasAny && !(map.en || '').trim()) {
    return `${what}: English is required when translations are set (it is the fallback)`;
  }
  return null;
};
/** Visibility "Who sees it" segments — one segmented control, single choice. */
const BANNER_BACKDROP_SEGMENTS: { value: BannerBackdrop; label: string; hint: string }[] = [
  { value: 'none', label: 'None', hint: 'The text sits straight on the artwork — only safe over a photo that is already dark and quiet at the bottom.' },
  { value: 'gradient', label: 'Gradient', hint: 'A dark fade up from the bottom edge. The default, and what every banner drew before this choice existed.' },
  { value: 'blur', label: 'Blur', hint: 'A frosted band behind the text, starting at a hard edge. Android draws a plain dark band instead — it cannot blur cheaply.' },
];

/** What the picker opens on when a tint is first added — mid-neutral, so the wheel
 *  starts somewhere an admin can move in any direction from. */
const DEFAULT_BANNER_TINT = '#808080';

/** The app's fixed alpha for the tint layer (ArticleCoverCard's styles.backdropColor). */
const BANNER_TINT_ALPHA = 0.5;

const hexToRgba = (hex: string, alpha: number): string => {
  const [r, g, b] = [1, 3, 5].map((i) => parseInt(hex.slice(i, i + 2), 16));
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

/**
 * The frosted band's background once a tint is picked, stacked the way the card
 * draws it: the admin's colour at the app's alpha, the light material over it, and
 * the band's own black on top.
 *
 * The app puts the colour under the blur, so the frost carries it; CSS can only
 * paint a background over its own `backdrop-filter`. The stacking therefore differs
 * by a hair, but the tone this shows is the tone the card draws — including how much
 * of the colour the light material washes out, which is the point of showing it.
 */
const blurBandBackground = (hex: string): string => [
  'linear-gradient(0deg, rgba(0, 0, 0, 0.25), rgba(0, 0, 0, 0.25))',
  'linear-gradient(0deg, rgba(255, 255, 255, 0.6), rgba(255, 255, 255, 0.6))',
  hexToRgba(hex, BANNER_TINT_ALPHA),
].join(', ');

const LOGIN_STATE_SEGMENTS: { value: LoginState; label: string }[] = [
  { value: 'all', label: 'Everyone' },
  { value: 'guest', label: 'Guests only' },
  { value: 'loggedIn', label: 'Logged-in only' },
];

const PLATFORMS: { id: PlatformId; label: string }[] = [
  { id: 'ios', label: 'iOS' },
  { id: 'android', label: 'Android' },
];

const ACTION_OPTIONS: { value: BannerActionType; label: string; description: string }[] = [
  { value: 'blend', label: 'Blend', description: 'Opens the camera with Blend mode selected' },
  { value: 'rebuild', label: 'Rebuild', description: 'Opens the camera with Rebuild mode selected' },
  { value: 'upscale', label: 'Upscale', description: 'Opens the upscale screen and the photo album, to upscale a photo from the phone' },
  { value: 'subscription', label: 'Subscription Page', description: 'Opens the subscription plan picker' },
  { value: 'topup', label: 'Topup Page', description: 'Credit top-up (free users get the plan picker instead)' },
  { value: 'webSubscription', label: 'Subscription Page (Web)', description: 'Opens the user portal\'s subscription page in the in-app browser' },
  { value: 'webTopup', label: 'Topup Page (Web)', description: 'Opens the user portal\'s top-up page in the in-app browser' },
  { value: 'extensionList', label: 'Lens List', description: 'Opens the lens list tab on a category' },
  { value: 'extension', label: 'Lens Detail', description: 'Opens a specific lens\'s detail page' },
  { value: 'creator', label: 'Creator Profile', description: 'Opens a specific user\'s profile' },
  { value: 'article', label: 'Article', description: 'Opens a specific article. Hidden in the app while that article is unpublished or outside its schedule' },
  { value: 'referral', label: 'Invite Friends', description: 'Opens the invite friends (referral) page' },
  { value: 'url', label: 'Website', description: 'Opens a website in the phone\'s browser, outside the app' },
];

/**
 * Mirrors the backend's `url` action rule: an absolute http(s) address. Kept
 * here so the admin sees the error before the save round trip.
 */
/** Host shown on the preview's action chip — the full URL wouldn't fit. */
const actionUrlHost = (value: string): string => {
  try {
    return new URL(value.trim()).host;
  } catch {
    return value;
  }
};

const isWebUrl = (value?: string): boolean => {
  const trimmed = (value || '').trim();
  if (!trimmed) return false;
  try {
    const { protocol } = new URL(trimmed);
    return protocol === 'http:' || protocol === 'https:';
  } catch {
    return false;
  }
};

/** Draggable element types. Top 10 / Top Category share the backend type. */
const ADD_OPTIONS: { key: string; label: string; icon: JSX.Element; create: () => Partial<LayoutElement> & { type: ElementType } }[] = [
  { key: 'article', label: 'Article', icon: <FiBook />, create: () => ({ type: 'article' }) },
  { key: 'banner', label: 'Banner', icon: <FiZap />, create: () => ({ type: 'banner', banner: { action: { type: 'blend' } } }) },
  { key: 'quickActions', label: 'Quick Actions', icon: <FiCommand />, create: () => ({ type: 'quickActions', quickActions: [{ label: { en: '' }, action: { type: 'blend' } }] }) },
  { key: 'carousel', label: 'Carousel', icon: <FiColumns />, create: () => ({ type: 'carousel', carouselItems: [{ label: { en: '' }, action: { type: 'blend' } }], infiniteScroll: true }) },
  { key: 'recentFraw', label: 'Recent FRAW', icon: <FiClock />, create: () => ({ type: 'recentFraw' }) },
  { key: 'topExtensions', label: 'Top Lenses', icon: <FiTrendingUp />, create: () => ({ type: 'topExtensions', limit: 10, displayStyle: 'horizontal' }) },
  { key: 'curatedExtensions', label: 'Lenses List', icon: <FiStar />, create: () => ({ type: 'curatedExtensions', extensions: [], displayStyle: 'horizontal' }) },
  { key: 'extensionGrid', label: 'Lens Grid', icon: <FiGrid />, create: () => ({ type: 'extensionGrid', limit: null }) },
];

const elementLabel = (el: LayoutElement): string => {
  switch (el.type) {
    case 'article': return 'Article';
    case 'banner': return 'Banner';
    case 'quickActions': return 'Quick Actions';
    // "(wide)" only when the flag actually changes anything — at two items
    case 'carousel': return el.wideMode && el.carouselItems?.length === 2 ? 'Carousel (wide)' : 'Carousel';
    case 'recentFraw': return 'Recent FRAW';
    case 'topExtensions': return el.displayStyle === 'vertical' ? 'Top Lenses (list)' : 'Top Lenses';
    case 'curatedExtensions': return el.displayStyle === 'vertical' ? 'Lenses List (vertical)' : 'Lenses List';
    case 'extensionGrid': return el.limit == null ? 'Extension Grid (∞)' : `Extension Grid (${el.limit})`;
  }
};

/** The tile-list field an element type stores its QuickActions under, if any. */
const tilesFieldOf = (type: ElementType): TilesField | null =>
  type === 'quickActions' ? 'quickActions' : type === 'carousel' ? 'carouselItems' : null;

const newElementId = () => `el-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
const newLayoutId = () => `layout-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;

export default function HomeLayoutPage() {
  const [layouts, setLayouts] = useState<AdminLayout[]>([]);
  const [articles, setArticles] = useState<ArticleOption[]>([]);
  const [categories, setCategories] = useState<CategoryOption[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [savedAt, setSavedAt] = useState<string | null>(null);

  // Modal draft — content + visibility edited here; only Confirm applies it
  const [editing, setEditing] = useState<EditingState | null>(null);
  const [uploading, setUploading] = useState(false);

  // Which insert position's element-type picker is open (null = none)
  const [insertPicker, setInsertPicker] = useState<{ layoutId: string; index: number } | null>(null);

  // Which localized-text translations modal is open (null = none): a tile
  // row's label or description, the element's section title or (quickActions)
  // its supporting description, or the banner overlay title
  const [labelEditor, setLabelEditor] = useState<LabelEditorTarget | null>(null);
  // Lenses List: the grid picker over the element modal
  const [lensPickerOpen, setLensPickerOpen] = useState(false);
  // Lenses List: the picked lens being dragged, and the gap it would drop into
  // (gap i = before lens i; picked.length = after the last). null gap = no-op spot.
  const [lensDrag, setLensDrag] = useState<{ from: number; gap: number | null } | null>(null);
  // That text as it stood when the modal opened: its inputs write straight
  // into the draft, so Cancel puts this back. A ref, not state — nothing
  // renders from it, and it must be readable by the Cancel handler.
  const labelSnapshot = useRef<LocalizedLabel | undefined>(undefined);
  // renderLabelModal() builds that Cancel handler — it closes over the write-back
  // for whichever field is being translated — and parks it here so Escape can
  // run the same logic instead of a second copy of it.
  const cancelLabelEditorRef = useRef<() => void>(() => {});

  // Per-layout user assignment search. Kept apart from the element modal's
  // target search: this one lives on the layout card, not in a draft.
  // Which layout's assigned-users modal is open (null = none)
  const [usersModalLayoutId, setUsersModalLayoutId] = useState<string | null>(null);
  const [userSearchLayoutId, setUserSearchLayoutId] = useState<string | null>(null);
  const [userQuery, setUserQuery] = useState('');
  const [userResults, setUserResults] = useState<(AssignedUser & { email?: string })[]>([]);
  const [userSearching, setUserSearching] = useState(false);

  // Action target search (extension / creator pickers in the modal) — used by
  // the banner action and by quick action / carousel rows. For tile rows,
  // qaSearchIndex is the row whose search input is active.
  const [targetQuery, setTargetQuery] = useState('');
  const [targetResults, setTargetResults] = useState<TargetSearchResult[]>([]);
  const [targetSearching, setTargetSearching] = useState(false);
  const [qaSearchIndex, setQaSearchIndex] = useState<number | null>(null);

  // Drag state: a new element type from the left list, or an in-layout reorder
  const [dragging, setDragging] = useState<DragState | null>(null);
  // Reorder target as a GAP index (0..count): a horizontal line renders in
  // that gap to show where the dragged section will land.
  const [reorderHint, setReorderHint] = useState<{ layoutId: string; index: number } | null>(null);
  const [dropHint, setDropHint] = useState<{ layoutId: string; index: number } | null>(null);

  /**
   * Fetches the layouts plus the article/category pickers.
   * `background` skips the full-page loading state — the post-save refresh
   * uses it so the editor stays on screen instead of blanking out and
   * remounting (which reads as a page reload and loses scroll position).
   */
  const loadLayout = useCallback(async (background = false) => {
    try {
      if (!background) setLoading(true);
      setError(null);
      // Note: apiRequest unwraps the { success, data } envelope for object payloads,
      // so the layouts arrive flattened as { success, layouts }. Array payloads
      // (articles, categories) keep their `data` field.
      const [layoutRes, articlesRes, categoriesRes] = await Promise.all([
        apiRequest<{ success: boolean; layouts?: AdminLayout[]; error?: string }>('/api/admin/home-layout'),
        apiRequest<{ success: boolean; data: any[] }>('/api/admin/articles'),
        apiRequest<{ success: boolean; data: CategoryOption[] }>('/api/admin/categories?page=1&limit=100'),
      ]);

      if (layoutRes.success) {
        setLayouts((layoutRes.layouts || []).map((layout) => ({
          ...layout,
          assignedUserIds: layout.assignedUserIds || [],
          assignedUsers: layout.assignedUsers || [],
          // The retired intro card is dropped so the next save removes it
          elements: (layout.elements || []).filter((el) => (el.type as string) !== 'introCard').map((el) => ({
            ...el,
            // Legacy elements (pre displayStyle) keep their original look
            ...(el.type === 'topExtensions' && !el.displayStyle && { displayStyle: el.categoryId ? 'vertical' as const : 'horizontal' as const }),
            ...(el.type === 'curatedExtensions' && { extensions: el.extensions || [] }),
            // Legacy tiles/titles (pre-i18n) stored plain strings
            ...(el.quickActions && { quickActions: el.quickActions.map((qa) => ({ ...qa, label: normalizeTileLabel(qa.label) })) }),
            ...(el.carouselItems && {
              carouselItems: el.carouselItems.map((item) => ({
                ...item,
                label: normalizeTileLabel(item.label),
              })),
            }),
            ...(el.title != null && { title: normalizeTileLabel(el.title) }),
            ...(el.description != null && { description: normalizeTileLabel(el.description) }),
            ...(el.banner?.title != null && { banner: { ...el.banner, title: normalizeTileLabel(el.banner.title) } }),
          })),
        })));
      } else {
        setError('Failed to load home layouts');
      }
      if (articlesRes.success) {
        setArticles(
          (articlesRes.data || []).map((article) => {
            const cover = article.sections?.[0];
            return {
              articleId: article.articleId,
              title: (cover?.type === 'articleCoverImage' && cover.title) || article.title || article.articleId,
              coverImageUrl: cover?.imageUrl || cover?.thumbnailUrl,
            };
          })
        );
      }
      if (categoriesRes.success) {
        setCategories(categoriesRes.data || []);
      }
    } catch (err) {
      console.error('Error loading home layouts:', err);
      setError(err instanceof Error ? err.message : 'Failed to load home layouts');
    } finally {
      if (!background) setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadLayout();
  }, [loadLayout]);

  // Debounced action target search (Extension Detail / Creator Profile) — for
  // the banner action, or the tile row whose search input is active
  useEffect(() => {
    const draft = editing?.draft;
    const tilesField = draft ? tilesFieldOf(draft.type) : null;
    const searchAction = draft?.type === 'banner'
      ? draft.banner?.action
      : tilesField && qaSearchIndex !== null
        ? draft?.[tilesField]?.[qaSearchIndex]?.action
        : undefined;
    const kind = searchAction?.type;
    if ((kind !== 'extension' && kind !== 'creator') || targetQuery.trim().length < 2) {
      setTargetResults([]);
      return;
    }
    const debounce = setTimeout(async () => {
      try {
        setTargetSearching(true);
        if (kind === 'extension') {
          const res = await apiRequest<{
            success: boolean;
            data: { _id: string; Name: string; Creator?: string; creatorName?: string }[];
          }>(`/api/admin/notifications/search-extensions?q=${encodeURIComponent(targetQuery)}`);
          if (res.success) {
            setTargetResults((res.data || []).map((e) => {
              // Creator is a user id; creatorName is the display name the backend resolved
              const creator = e.creatorName || e.Creator;
              return { id: e._id, name: e.Name, sub: creator ? `by ${creator}` : undefined };
            }));
          }
        } else {
          const res = await apiRequest<{ success: boolean; data: { userId: string; name: string }[] }>(
            `/api/admin/notifications/search-users?q=${encodeURIComponent(targetQuery)}`
          );
          if (res.success) {
            setTargetResults((res.data || []).map((u) => ({ id: u.userId, name: u.name })));
          }
        }
      } catch (err) {
        console.error('Error searching banner action target:', err);
      } finally {
        setTargetSearching(false);
      }
    }, 300);
    return () => clearTimeout(debounce);
  }, [targetQuery, editing, qaSearchIndex]);

  const resetTargetSearch = () => {
    setTargetQuery('');
    setTargetResults([]);
    setQaSearchIndex(null);
  };

  // Debounced user search for the layout assignment box
  useEffect(() => {
    if (!userSearchLayoutId || userQuery.trim().length < 2) {
      setUserResults([]);
      return;
    }
    const debounce = setTimeout(async () => {
      try {
        setUserSearching(true);
        const res = await apiRequest<{ success: boolean; data: { userId: string; name: string; email?: string }[] }>(
          `/api/admin/notifications/search-users?q=${encodeURIComponent(userQuery)}`
        );
        if (res.success) {
          setUserResults((res.data || []).map((u) => ({ userId: u.userId, name: u.name, email: u.email })));
        }
      } catch (err) {
        console.error('Error searching users to assign:', err);
      } finally {
        setUserSearching(false);
      }
    }, 300);
    return () => clearTimeout(debounce);
  }, [userQuery, userSearchLayoutId]);

  /**
   * Assigns a user to a layout. The backend refuses the same user on two
   * layouts, so they are taken off any other one here rather than letting the
   * save fail on a conflict the admin cannot see.
   */
  const assignUser = (layoutId: string, user: AssignedUser) => {
    setLayouts((prev) => prev.map((layout) => {
      const withoutUser = {
        ...layout,
        assignedUserIds: (layout.assignedUserIds || []).filter((id) => id !== user.userId),
        assignedUsers: (layout.assignedUsers || []).filter((u) => u.userId !== user.userId),
      };
      if (layout.layoutId !== layoutId) return withoutUser;
      return {
        ...withoutUser,
        assignedUserIds: [...withoutUser.assignedUserIds, user.userId],
        assignedUsers: [...withoutUser.assignedUsers, user],
      };
    }));
    setUserQuery('');
    setUserResults([]);
    markDirty();
  };

  const unassignUser = (layoutId: string, userId: string) => {
    mutateLayout(layoutId, (layout) => ({
      ...layout,
      assignedUserIds: (layout.assignedUserIds || []).filter((id) => id !== userId),
      assignedUsers: (layout.assignedUsers || []).filter((user) => user.userId !== userId),
    }));
  };

  const markDirty = () => {
    setDirty(true);
    setSavedAt(null);
  };

  // ---------------------------------------------------------------------------
  // Layout operations
  // ---------------------------------------------------------------------------

  const mutateLayout = (layoutId: string, mutate: (layout: AdminLayout) => AdminLayout) => {
    setLayouts((prev) => prev.map((layout) => (layout.layoutId === layoutId ? mutate(layout) : layout)));
    markDirty();
  };

  const addLayout = () => {
    setLayouts((prev) => [
      ...prev,
      {
        layoutId: newLayoutId(),
        name: `Layout ${prev.length + 1}`,
        // The very first layout becomes active by default
        isActive: prev.length === 0,
        assignedUserIds: [],
        elements: [],
      },
    ]);
    markDirty();
  };

  const removeLayout = (layoutId: string) => {
    const layout = layouts.find((l) => l.layoutId === layoutId);
    if (!layout) return;
    // eslint-disable-next-line no-restricted-globals
    if (!window.confirm(`Delete layout "${layout.name || 'Untitled'}"?${layout.isActive ? ' It is currently ACTIVE — the app will fall back to its built-in home screen.' : ''}`)) {
      return;
    }
    setLayouts((prev) => prev.filter((l) => l.layoutId !== layoutId));
    markDirty();
  };

  const setActiveLayout = (layoutId: string, active: boolean) => {
    setLayouts((prev) => prev.map((layout) => ({
      ...layout,
      // Activating one deactivates the others; deactivating leaves none active
      isActive: active ? layout.layoutId === layoutId : (layout.layoutId === layoutId ? false : layout.isActive),
    })));
    markDirty();
  };

  // ---------------------------------------------------------------------------
  // Modal draft handling
  // ---------------------------------------------------------------------------

  const openAddModal = (create: () => Partial<LayoutElement> & { type: ElementType }, layoutId: string, insertAt?: number) => {
    setInsertPicker(null);
    resetTargetSearch();
    setEditing({
      mode: 'add',
      layoutId,
      insertAt,
      draft: {
        elementId: newElementId(),
        visibility: { enabled: true, loginState: 'all', plans: [], platforms: [] },
        ...create(),
      },
    });
  };

  const openEditModal = (layoutId: string, element: LayoutElement) => {
    resetTargetSearch();
    // Deep copy so modal edits don't touch the preview until Confirm
    setEditing({ mode: 'edit', layoutId, draft: JSON.parse(JSON.stringify(element)) });
  };

  /**
   * Opens the translations modal on a piece of localized text, snapshotting it
   * first so Cancel can restore it. Every field opens on both focus and click,
   * so only the first of those (the one that finds the modal closed) may take
   * the snapshot — a second one would capture the edits it is meant to undo.
   */
  const openLabelEditor = (target: LabelEditorTarget) => {
    if (!labelEditor) {
      labelSnapshot.current = editing ? readLabelEditorValue(target, editing.draft) : undefined;
    }
    setLabelEditor(target);
  };

  const updateDraft = (updates: Partial<LayoutElement>) => {
    setEditing((prev) => (prev ? { ...prev, draft: { ...prev.draft, ...updates } } : prev));
  };

  const updateDraftVisibility = (updates: Partial<Visibility>) => {
    setEditing((prev) =>
      prev ? { ...prev, draft: { ...prev.draft, visibility: { ...prev.draft.visibility, ...updates } } } : prev
    );
  };

  const updateDraftBanner = (updates: Partial<BannerConfig>) => {
    setEditing((prev) =>
      prev ? { ...prev, draft: { ...prev.draft, banner: { ...(prev.draft.banner as BannerConfig), ...updates } } } : prev
    );
  };

  const updateDraftTile = (field: TilesField, index: number, updates: Partial<QuickAction>) => {
    setEditing((prev) => {
      if (!prev) return prev;
      const tiles = [...(prev.draft[field] || [])];
      tiles[index] = { ...tiles[index], ...updates };
      return { ...prev, draft: { ...prev.draft, [field]: tiles } };
    });
  };

  const addDraftTile = (field: TilesField) => {
    setEditing((prev) =>
      prev
        ? { ...prev, draft: { ...prev.draft, [field]: [...(prev.draft[field] || []), { label: { en: '' }, action: { type: 'blend' as const } }] } }
        : prev
    );
  };

  const removeDraftTile = (field: TilesField, index: number) => {
    resetTargetSearch();
    setLabelEditor(null);
    setEditing((prev) =>
      prev
        ? { ...prev, draft: { ...prev.draft, [field]: (prev.draft[field] || []).filter((_, i) => i !== index) } }
        : prev
    );
  };

  const validateDraft = (draft: LayoutElement): string | null => {
    const sectionTitleError = localizedTitleError(draft.title, 'Section title');
    if (sectionTitleError) return sectionTitleError;
    // Section titles carry color markup: the tags have to be well-formed, and
    // the cap counts what a user actually reads, not the tags around it
    for (const lang of LABEL_LANGUAGES) {
      const text = (draft.title?.[lang.code] || '').trim();
      if (!text) continue;
      const markupError = richTextError(text, `Section title (${lang.name})`);
      if (markupError) return markupError;
      if (visibleLength(text) > MAX_TITLE_LENGTH) {
        return `Section title (${lang.name}): at most ${MAX_TITLE_LENGTH} characters, not counting color tags`;
      }
    }
    const descriptionError = localizedTitleError(draft.description, 'Description');
    if (descriptionError) return descriptionError;
    if (draft.type === 'article' && !draft.articleId) return 'Pick an article';
    if (draft.type === 'curatedExtensions') {
      const count = draft.extensions?.length ?? 0;
      if (count === 0) return 'Pick at least one lens';
      if (count > MAX_CURATED_EXTENSIONS) return `At most ${MAX_CURATED_EXTENSIONS} lenses`;
    }
    if (draft.type === 'banner') {
      if (!draft.banner?.imageKey && !draft.banner?.videoKey) return 'Upload an image or video';
      const bannerTitleError = localizedTitleError(draft.banner.title, 'Banner title');
      if (bannerTitleError) return bannerTitleError;
      // Formatted like the section title, so the same two rules: well-formed tags,
      // and a cap on what a reader actually sees rather than on the markup
      for (const lang of LABEL_LANGUAGES) {
        const text = (draft.banner.title?.[lang.code] || '').trim();
        if (!text) continue;
        const markupError = richTextError(text, `Banner title (${lang.name})`);
        if (markupError) return markupError;
        if (visibleLength(text) > MAX_TITLE_LENGTH) {
          return `Banner title (${lang.name}): at most ${MAX_TITLE_LENGTH} characters, not counting color tags`;
        }
      }
      const bannerSubtitleError = localizedTitleError(draft.banner.subtitle, 'Banner subtitle');
      if (bannerSubtitleError) return bannerSubtitleError;
      for (const lang of LABEL_LANGUAGES) {
        const text = (draft.banner.subtitle?.[lang.code] || '').trim();
        if (!text) continue;
        const markupError = richTextError(text, `Banner subtitle (${lang.name})`);
        if (markupError) return markupError;
        if (visibleLength(text) > MAX_BANNER_SUBTITLE) {
          return `Banner subtitle (${lang.name}): at most ${MAX_BANNER_SUBTITLE} characters, not counting color tags`;
        }
      }
      const bannerButtonError = localizedTitleError(draft.banner.buttonLabel, 'Banner button');
      if (bannerButtonError) return bannerButtonError;
      for (const lang of LABEL_LANGUAGES) {
        const text = (draft.banner.buttonLabel?.[lang.code] || '').trim();
        if (text.length > MAX_BANNER_BUTTON_LABEL) {
          return `Banner button (${lang.name}): at most ${MAX_BANNER_BUTTON_LABEL} characters`;
        }
      }
      if (draft.banner.action.type === 'extensionList' && !draft.banner.action.categoryId) {
        return 'Select a category for the Lens List action';
      }
      if (draft.banner.action.type === 'extension' && !draft.banner.action.extensionId) {
        return 'Pick a lens for the Lens Detail action';
      }
      if (draft.banner.action.type === 'creator' && !draft.banner.action.userId) {
        return 'Pick a user for the Creator Profile action';
      }
      if (draft.banner.action.type === 'article' && !draft.banner.action.articleId) {
        return 'Pick an article for the Article action';
      }
      if (draft.banner.action.type === 'url' && !isWebUrl(draft.banner.action.url)) {
        return 'Enter a website address starting with http:// or https:// for the Website action';
      }
    }
    const tilesField = tilesFieldOf(draft.type);
    if (tilesField) {
      const noun = draft.type === 'carousel' ? 'Carousel item' : 'Quick action';
      const tiles = draft[tilesField] || [];
      if (tiles.length === 0) return `Add at least one ${noun.toLowerCase()}`;
      for (let i = 0; i < tiles.length; i++) {
        const tile = tiles[i];
        const where = `${noun} ${i + 1}`;
        if (!tile.imageKey) return `${where}: upload an image`;
        // A carousel card may be artwork with no caption, so an entirely empty
        // label is allowed there — but a label with translations and no English
        // is still wrong, since English is the app's fallback.
        const labelSet = LABEL_LANGUAGES.some((lang) => (tile.label?.[lang.code] || '').trim());
        if (!(tile.label?.en || '').trim() && (draft.type !== 'carousel' || labelSet)) {
          return `${where}: enter an English label`;
        }
        for (const lang of LABEL_LANGUAGES) {
          const text = (tile.label?.[lang.code] || '').trim();
          if (!text) continue;
          // A carousel caption is formatted text measured by what a user reads;
          // a quick action's label is a short plain one
          if (draft.type === 'carousel') {
            const markupError = richTextError(text, `${where}: ${lang.name} caption`);
            if (markupError) return markupError;
            if (visibleLength(text) > MAX_CAROUSEL_CAPTION) {
              return `${where}: ${lang.name} caption must be at most ${MAX_CAROUSEL_CAPTION} characters`;
            }
          } else if (text.length > MAX_QUICK_ACTION_LABEL) {
            return `${where}: ${lang.name} label must be at most ${MAX_QUICK_ACTION_LABEL} characters`;
          }
        }
        if (tile.action.type === 'extensionList' && !tile.action.categoryId) return `${where}: select a category for the Lens List action`;
        if (tile.action.type === 'extension' && !tile.action.extensionId) return `${where}: pick an extension for the Extension Detail action`;
        if (tile.action.type === 'creator' && !tile.action.userId) return `${where}: pick a user for the Creator Profile action`;
        if (tile.action.type === 'article' && !tile.action.articleId) return `${where}: pick an article for the Article action`;
        if (tile.action.type === 'url' && !isWebUrl(tile.action.url)) return `${where}: enter a website address starting with http:// or https:// for the Website action`;
      }
    }
    return null;
  };

  const confirmModal = () => {
    if (!editing) return;
    const { mode, draft, layoutId } = editing;

    const targetLayout = layouts.find((l) => l.layoutId === layoutId);
    if (!targetLayout) {
      setEditing(null);
      return;
    }

    const validationError = validateDraft(draft);
    if (validationError) {
      alert(validationError);
      return;
    }

    const isInfiniteGrid = draft.type === 'extensionGrid' && draft.limit == null;
    const otherInfiniteExists = targetLayout.elements.some(
      (el) => el.elementId !== draft.elementId && el.type === 'extensionGrid' && el.limit == null
    );
    if (isInfiniteGrid && otherInfiniteExists) {
      alert('Only one infinite lens grid is allowed per layout — set a count on this one, or edit the existing infinite grid instead.');
      return;
    }
    if (mode === 'edit' && isInfiniteGrid &&
        targetLayout.elements.findIndex((el) => el.elementId === draft.elementId) !== targetLayout.elements.length - 1) {
      alert('An infinite lens grid must be the last element — move it to the end first, or give it a count.');
      return;
    }

    mutateLayout(layoutId, (layout) => {
      if (mode === 'edit') {
        return { ...layout, elements: layout.elements.map((el) => (el.elementId === draft.elementId ? draft : el)) };
      }
      const next = [...layout.elements];
      let insertAt = Math.min(editing.insertAt ?? next.length, next.length);
      if (isInfiniteGrid) {
        // An infinite grid always goes last, wherever it was requested
        insertAt = next.length;
      } else {
        // Never insert after a trailing infinite grid
        const lastIsInfiniteGrid =
          next.length > 0 && next[next.length - 1].type === 'extensionGrid' && next[next.length - 1].limit == null;
        if (lastIsInfiniteGrid && insertAt >= next.length) {
          insertAt = next.length - 1;
        }
      }
      next.splice(insertAt, 0, draft);
      return { ...layout, elements: next };
    });
    setEditing(null);
    setLabelEditor(null);
  };

  const cancelModal = () => {
    setEditing(null);
    setLabelEditor(null);
  };

  // ---------------------------------------------------------------------------
  // Element list operations (per layout)
  // ---------------------------------------------------------------------------

  const removeElement = (layoutId: string, index: number) => {
    mutateLayout(layoutId, (layout) => ({ ...layout, elements: layout.elements.filter((_, i) => i !== index) }));
  };

  const moveElement = (layoutId: string, index: number, direction: 'up' | 'down') => {
    mutateLayout(layoutId, (layout) => {
      const newIndex = direction === 'up' ? index - 1 : index + 1;
      if (newIndex < 0 || newIndex >= layout.elements.length) return layout;
      const next = [...layout.elements];
      [next[index], next[newIndex]] = [next[newIndex], next[index]];
      return { ...layout, elements: next };
    });
  };

  // Reorder-by-drag within one layout. The drop position is a gap index —
  // hovering the top half of a section targets the gap above it, the bottom
  // half the gap below.
  const handleReorderStart = (layoutId: string, index: number) => setDragging({ kind: 'reorder', layoutId, index });

  const setReorderGap = (layoutId: string, gap: number) => {
    if (dragging?.kind !== 'reorder') return;
    // Dropping into the gap just above or below itself is a no-op — show no line
    if (gap === dragging.index || gap === dragging.index + 1) {
      setReorderHint(null);
    } else {
      setReorderHint({ layoutId, index: gap });
    }
  };

  const handleReorderOver = (e: React.DragEvent, layoutId: string, index: number) => {
    if (dragging?.kind !== 'reorder' || dragging.layoutId !== layoutId) return;
    e.preventDefault();
    const rect = e.currentTarget.getBoundingClientRect();
    const gap = e.clientY < rect.top + rect.height / 2 ? index : index + 1;
    setReorderGap(layoutId, gap);
  };

  const handleReorderEnd = () => {
    if (dragging?.kind === 'reorder' && reorderHint && reorderHint.layoutId === dragging.layoutId) {
      const { layoutId, index: from } = dragging;
      const gap = reorderHint.index;
      if (gap !== from && gap !== from + 1) {
        mutateLayout(layoutId, (layout) => {
          const next = [...layout.elements];
          const [moved] = next.splice(from, 1);
          // Removing the dragged item shifts gaps after it down by one
          next.splice(gap > from ? gap - 1 : gap, 0, moved);
          return { ...layout, elements: next };
        });
      }
    }
    setDragging(null);
    setReorderHint(null);
  };

  // New-element drop (from the left list) onto a layout
  const handleNewElementDrop = (layoutId: string, insertAt: number) => {
    if (dragging?.kind !== 'new') return;
    const option = ADD_OPTIONS.find((o) => o.key === dragging.typeKey);
    setDragging(null);
    setDropHint(null);
    if (option) {
      openAddModal(option.create, layoutId, insertAt);
    }
  };

  /** Uploads media to the shared home-layout bucket; returns the upload payload or null on failure. */
  const uploadLayoutMedia = async (file: File, kind: 'image' | 'video'): Promise<Record<string, any> | null> => {
    try {
      setUploading(true);

      let accessToken = getAccessToken();
      if (isTokenExpired()) {
        await refreshAccessToken();
        accessToken = getAccessToken();
      }

      const formData = new FormData();
      formData.append(kind, file);

      const response = await fetch(`${API_URL}/api/admin/home-layout/upload-${kind}`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${accessToken}` },
        body: formData,
      });

      const data = await response.json();
      if (!data.success) {
        alert(`Failed to upload ${kind}: ` + (data.error?.message || data.error || 'Unknown error'));
        return null;
      }
      return data.data;
    } catch (err) {
      console.error(`Error uploading ${kind}:`, err);
      alert(`Failed to upload ${kind}`);
      return null;
    } finally {
      setUploading(false);
    }
  };

  const handleBannerUpload = async (file: File, kind: 'image' | 'video') => {
    const data = await uploadLayoutMedia(file, kind);
    if (!data) return;
    if (kind === 'image') {
      updateDraftBanner({
        imageKey: data.imageKey,
        imageUrl: data.imageUrl,
        videoKey: undefined,
        videoUrl: undefined,
        thumbnailKey: undefined,
        thumbnailUrl: undefined,
        hasAudio: undefined,
        ...(data.aspectRatio !== undefined && { aspectRatio: data.aspectRatio }),
      });
    } else {
      updateDraftBanner({
        videoKey: data.videoKey,
        videoUrl: data.videoUrl,
        imageKey: undefined,
        imageUrl: undefined,
        // Cleared first: a previous video's answer must not survive onto this one when
        // the probe of this one came back inconclusive
        hasAudio: undefined,
        ...(data.thumbnailKey && { thumbnailKey: data.thumbnailKey }),
        ...(data.thumbnailUrl && { thumbnailUrl: data.thumbnailUrl }),
        ...(data.aspectRatio !== undefined && { aspectRatio: data.aspectRatio }),
        ...(data.hasAudio !== undefined && { hasAudio: data.hasAudio }),
      });
    }
  };

  const handleTileImageUpload = async (field: TilesField, index: number, file: File) => {
    const data = await uploadLayoutMedia(file, 'image');
    if (!data) return;
    updateDraftTile(field, index, { imageKey: data.imageKey, imageUrl: data.imageUrl });
  };

  const validate = (): string | null => {
    for (const layout of layouts) {
      const layoutName = layout.name || 'Untitled layout';
      for (let i = 0; i < layout.elements.length; i++) {
        const el = layout.elements[i];
        const where = `${layoutName}, element ${i + 1} (${elementLabel(el)})`;
        const draftError = validateDraft(el);
        if (draftError) return `${where}: ${draftError}`;
        if (el.type === 'extensionGrid' && el.limit == null && i !== layout.elements.length - 1) {
          return `${where}: an infinite grid must be the last element`;
        }
      }
    }
    return null;
  };

  const handleSave = async () => {
    const validationError = validate();
    if (validationError) {
      alert(validationError);
      return;
    }

    try {
      setSaving(true);
      setError(null);
      const payload = {
        layouts: layouts.map((layout) => ({
          layoutId: layout.layoutId,
          name: layout.name,
          isActive: layout.isActive,
          assignedUserIds: layout.assignedUserIds || [],
          elements: layout.elements.map((el) => ({
            elementId: el.elementId,
            type: el.type,
            ...(el.articleId && { articleId: el.articleId }),
            ...(el.banner && {
              banner: {
                imageKey: el.banner.imageKey,
                videoKey: el.banner.videoKey,
                thumbnailKey: el.banner.thumbnailKey,
                title: cleanOptionalText(el.banner.title),
                subtitle: cleanOptionalText(el.banner.subtitle),
                buttonLabel: cleanOptionalText(el.banner.buttonLabel),
                aspectRatio: el.banner.aspectRatio,
                hasAudio: el.banner.hasAudio,
                backdrop: el.banner.backdrop,
                backdropColor: el.banner.backdropColor,
                action: el.banner.action,
              },
            }),
            ...(el.quickActions && {
              quickActions: el.quickActions.map((qa) => ({
                imageKey: qa.imageKey,
                label: cleanTileLabel(qa.label),
                action: qa.action,
              })),
            }),
            ...(el.carouselItems && {
              carouselItems: el.carouselItems.map((item) => ({
                imageKey: item.imageKey,
                // Optional here, so an all-empty caption is sent as absent (the
                // key drops out of the JSON) rather than as an empty English one
                label: cleanOptionalText(item.label),
                action: item.action,
              })),
            }),
            ...(el.categoryId && { categoryId: el.categoryId }),
            ...(el.type === 'curatedExtensions' && { extensionIds: (el.extensions || []).map((ext) => ext.id) }),
            ...(el.title && { title: cleanOptionalText(el.title) }),
            // quickActions only — the backend drops it on other element types
            ...(el.type === 'quickActions' && el.description && { description: cleanOptionalText(el.description) }),
            ...(el.displayStyle && { displayStyle: el.displayStyle }),
            ...(typeof el.showRank === 'boolean' && { showRank: el.showRank }),
            // carousel only — the backend ignores them on other element types
            ...(el.type === 'carousel' && el.wideMode && { wideMode: true }),
            // Wrapping is the default, so only the opt-out is sent
            ...(el.type === 'carousel' && el.infiniteScroll === false && { infiniteScroll: false }),
            limit: el.limit ?? null,
            visibility: el.visibility,
          })),
        })),
      };

      const response = await apiRequest<{ success: boolean; error?: { message?: string } | string }>(
        '/api/admin/home-layout',
        { method: 'PUT', body: JSON.stringify(payload) }
      );

      if (response.success) {
        setDirty(false);
        setSavedAt(new Date().toLocaleTimeString());
        // Refresh admin display extras (article titles/covers) in place —
        // no loading state, so the editor doesn't blank out mid-session
        await loadLayout(true);
      } else {
        const message = typeof response.error === 'string' ? response.error : response.error?.message;
        setError(message || 'Failed to save layouts');
      }
    } catch (err) {
      console.error('Error saving home layouts:', err);
      setError(err instanceof Error ? err.message : 'Failed to save layouts');
    } finally {
      setSaving(false);
    }
  };

  // ---------------------------------------------------------------------------
  // Unsaved-changes guard
  // ---------------------------------------------------------------------------

  /**
   * Holds back any in-app navigation (sidebar link, Logout, browser Back)
   * while the editor has an unsaved draft — the prompt below then offers Save /
   * Discard / Stay. Reload and tab close can't be intercepted this way, so
   * `beforeunload` covers those with the browser's own dialog.
   */
  const blocker = useBlocker(dirty);

  useEffect(() => {
    if (!dirty) return;
    const warnOnUnload = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      // Required by older browsers; modern ones show their own wording
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', warnOnUnload);
    return () => window.removeEventListener('beforeunload', warnOnUnload);
  }, [dirty]);

  // "Save & leave": the save clears `dirty`, which releases the held navigation
  useEffect(() => {
    if (!dirty && blocker.state === 'blocked') blocker.proceed();
  }, [dirty, blocker]);

  // Escape peels off one layer at a time, so it's declared here bottom-up: the
  // type picker, the element modal, the translations modal stacked on top of it,
  // and finally the leave prompt. Each one repeats what its own Cancel does —
  // including refusing to budge mid-upload or mid-save.
  useEscapeToClose(insertPicker !== null, () => setInsertPicker(null));
  useEscapeToClose(usersModalLayoutId !== null, () => setUsersModalLayoutId(null));
  useEscapeToClose(!!editing, () => { if (!uploading) cancelModal(); });
  useEscapeToClose(!!labelEditor, () => cancelLabelEditorRef.current());

  // The picker lives inside the element modal; it never outlives it
  useEffect(() => {
    if (!editing) setLensPickerOpen(false);
  }, [editing]);
  useEscapeToClose(blocker.state === 'blocked', () => {
    // Re-checked inside the callback: `reset` only exists on a blocked blocker.
    if (!saving && blocker.state === 'blocked') blocker.reset();
  });

  const renderLeavePrompt = () => {
    if (blocker.state !== 'blocked') return null;
    return (
      <div className="hl-modal-overlay hl-leave-overlay">
        <div className="hl-modal hl-leave-modal">
          <h3 className="hl-modal-title"><FiAlertTriangle /> Unsaved changes</h3>
          <div className="hl-modal-body">
            This layout has changes you haven't saved yet. Save them before leaving, or they will be lost.
          </div>
          <div className="hl-modal-actions">
            <button type="button" className="hl-cancel-btn" onClick={() => blocker.reset()} disabled={saving}>
              Stay on page
            </button>
            <button type="button" className="hl-discard-btn" onClick={() => blocker.proceed()} disabled={saving}>
              Leave without saving
            </button>
            <button type="button" className="hl-confirm-btn" onClick={handleSave} disabled={saving}>
              <FiSave /> {saving ? 'Saving…' : 'Save & leave'}
            </button>
          </div>
        </div>
      </div>
    );
  };

  const categoryName = (categoryId?: string): string => {
    if (!categoryId) return '';
    const category = categories.find((c) => c.category_id === categoryId);
    return category?.label.en || categoryId;
  };

  /** Title of an article action's target: the loaded list first, then the backend-resolved name. */
  const articleTitle = (articleId: string, targetName?: string): string =>
    articles.find((a) => a.articleId === articleId)?.title || targetName || articleId;

  /** Article picker for an `article` tap action — shared by the banner and tile editors. */
  const renderArticleActionSelect = (action: BannerAction, onChange: (action: BannerAction) => void) => (
    <select
      className="hl-input"
      value={action.articleId || ''}
      onChange={(e) => onChange({ type: 'article', articleId: e.target.value || undefined })}
    >
      <option value="">Select an article</option>
      {articles.map((article) => (
        <option key={article.articleId} value={article.articleId}>
          {article.title}
        </option>
      ))}
      {/* Keep a saved article selectable even if it no longer appears in the list */}
      {action.articleId && !articles.some((a) => a.articleId === action.articleId) && (
        <option value={action.articleId}>{action.targetName || action.articleId}</option>
      )}
    </select>
  );

  const articleInfo = (el: LayoutElement): { title: string; imageUrl?: string } => {
    const option = articles.find((a) => a.articleId === el.articleId);
    return {
      title: option?.title || el.article?.title || 'Pick an article',
      imageUrl: option?.coverImageUrl || el.article?.coverImageUrl || el.article?.coverThumbnailUrl,
    };
  };

  const visibilityBadges = (v: Visibility): string[] => {
    const badges: string[] = [];
    if (v.loginState === 'guest') badges.push('Guests');
    if (v.loginState === 'loggedIn') badges.push('Logged-in');
    if (v.plans.length > 0) badges.push(v.plans.join('/'));
    if (v.platforms.length > 0) badges.push(v.platforms.map((p) => (p === 'ios' ? 'iOS' : 'Android')).join('+'));
    return badges;
  };

  // ---------------------------------------------------------------------------
  // Phone preview renderers — mimic the mobile home screen
  // ---------------------------------------------------------------------------

  /**
   * Ranked strip preview; numbers render only when the element shows ranks.
   * `images` (a Lenses List's picks) fills the cards; a ranking has none to show.
   */
  const renderRankedRow = (count: number, showRank: boolean, images?: (string | undefined)[]) => (
    <div className="hp-ranked-row">
      {Array.from({ length: Math.min(count, showRank ? 3 : 4) }).map((_, i) => (
        <div key={i} className="hp-ranked-item">
          {showRank && <span className="hp-rank-number">{i + 1}</span>}
          {images?.[i]
            ? <img src={images[i]} alt="" className={`hp-card-placeholder hp-card-tall hp-preview-thumb${showRank ? '' : ' no-rank'}`} />
            : <div className={`hp-card-placeholder hp-card-tall${showRank ? '' : ' no-rank'}`} />}
        </div>
      ))}
      {showRank && <span className="hp-rank-number hp-rank-peek">{Math.min(count, 3) + 1}</span>}
    </div>
  );

  const renderPreviewContent = (el: LayoutElement) => {
    switch (el.type) {
      case 'article': {
        const info = articleInfo(el);
        return (
          <div className="hp-cover-card">
            {info.imageUrl
              ? <img src={info.imageUrl} alt="" className="hp-cover-media" />
              : <div className="hp-cover-media hp-cover-empty"><FiBook size={28} /></div>}
            <div className="hp-cover-overlay">
              <span className="hp-cover-title">{info.title}</span>
            </div>
          </div>
        );
      }

      case 'banner': {
        const banner = el.banner;
        const mediaUrl = banner?.imageUrl || banner?.thumbnailUrl;
        const action = ACTION_OPTIONS.find((a) => a.value === banner?.action.type);
        const hasBannerText = !!(banner?.title?.en || banner?.subtitle?.en || banner?.buttonLabel?.en);
        const bannerBackdrop = banner?.backdrop || 'gradient';
        return (
          <div className="hp-cover-card">
            {mediaUrl
              ? <img src={mediaUrl} alt="" className="hp-cover-media" />
              : <div className="hp-cover-media hp-cover-empty">{banner?.videoKey ? <FiVideo size={28} /> : <FiImage size={28} />}<span>Upload media</span></div>}
            {/* The overlay only positions. The band below carries whichever backdrop was
                picked and sits flush to the card's bottom edge, the way the app draws it;
                the chip is admin-only info, so it floats above the band rather than on it. */}
            <div className="hp-cover-overlay hp-cover-overlay-banner">
              <span className="hp-action-chip">
                <FiZap size={10} /> {action?.label || 'Action'}
                {banner?.action.type === 'extensionList' && banner.action.categoryId ? `: ${categoryName(banner.action.categoryId)}` : ''}
                {(banner?.action.type === 'extension' || banner?.action.type === 'creator') && banner.action.targetName ? `: ${banner.action.targetName}` : ''}
                {banner?.action.type === 'article' && banner.action.articleId ? `: ${articleTitle(banner.action.articleId, banner.action.targetName)}` : ''}
                {banner?.action.type === 'url' && banner.action.url ? `: ${actionUrlHost(banner.action.url)}` : ''}
              </span>
              {/* No text = nothing for a backdrop to sit behind, whichever one is picked */}
              {hasBannerText && (
                <div
                  className={`hp-cover-band hp-backdrop-${bannerBackdrop}`}
                  // Only the frosted band reads the tint, exactly as the app does
                  style={bannerBackdrop === 'blur' && banner?.backdropColor
                    ? { background: blurBandBackground(banner.backdropColor) }
                    : undefined}
                >
                  <div className="hp-cover-bottom">
                    <div className="hp-cover-text">
                      {/* Subtitle above the title, one line to the title's two — as on the card */}
                      {banner?.subtitle?.en && <span className="hp-cover-subtitle">{renderRichText(banner.subtitle.en)}</span>}
                      {banner?.title?.en && <span className="hp-cover-title">{renderRichText(banner.title.en)}</span>}
                    </div>
                    {/* Present = the only thing that runs the action, so the card is not tappable */}
                    {banner?.buttonLabel?.en && <span className="hp-cover-button">{banner.buttonLabel.en}</span>}
                  </div>
                </div>
              )}
            </div>
          </div>
        );
      }

      case 'quickActions': {
        const quickActions = el.quickActions || [];
        const renderTile = (qa: QuickAction, i: number) => {
          const action = ACTION_OPTIONS.find((a) => a.value === qa.action.type);
          return (
            <div key={i} className="hp-qa-tile" title={action?.label}>
              {qa.imageUrl
                ? <img src={qa.imageUrl} alt="" className="hp-qa-image" />
                : <div className="hp-qa-image hp-qa-image-empty"><FiImage size={16} /></div>}
              <span className="hp-qa-label">{qa.label?.en || '…'}</span>
            </div>
          );
        };

        // A title and/or description switches the app to the titled layout:
        // static text on the left, two tiles right-aligned, the rest scrolled
        // in. With more than those two the app parks the row further left so
        // the third is caught by the screen edge with a slice of it still
        // showing — that sliver is the whole affordance saying the row
        // scrolls, so the preview cuts the third tile the same way instead of
        // counting the remainder off to one side.
        if (el.title?.en || el.description?.en) {
          const hiddenCount = Math.max(0, quickActions.length - 2);
          return (
            <div className="hp-qa-titled">
              <div className="hp-qa-titled-text">
                {el.title?.en && <div className="hp-qa-titled-title">{renderRichText(el.title.en)}</div>}
                {el.description?.en && <div className="hp-qa-titled-desc">{el.description.en}</div>}
              </div>
              {quickActions.length === 0
                ? <div className="hp-qa-empty">Add quick actions</div>
                : (
                  <div
                    className={`hp-qa-titled-tiles${hiddenCount > 0 ? ' peek' : ''}`}
                    title={hiddenCount > 0 ? `${hiddenCount} more, reachable by scrolling` : undefined}
                  >
                    {quickActions.slice(0, hiddenCount > 0 ? 3 : 2).map(renderTile)}
                  </div>
                )}
            </div>
          );
        }

        return (
          <div className="hp-qa-row">
            {quickActions.length === 0 && <div className="hp-qa-empty">Add quick actions</div>}
            {quickActions.map(renderTile)}
          </div>
        );
      }

      case 'carousel': {
        const items = el.carouselItems || [];
        return (
          <div className="hp-section-block">
            {el.title?.en && <div className="hp-section-title">{renderRichText(el.title.en)}</div>}
            {/* One or two items fill the row; three or more — and a two-item
                carousel in wide mode — scroll at ~2/3 width */}
            <div className={`hp-carousel-row${items.length > 0 && items.length < 3 && !(el.wideMode && items.length === 2) ? ' fill' : ''}`}>
              {items.length === 0 && <div className="hp-qa-empty">Add carousel items</div>}
              {items.map((item, i) => {
                const action = ACTION_OPTIONS.find((a) => a.value === item.action.type);
                return (
                  <div key={i} className="hp-carousel-item" title={action?.label}>
                    {item.imageUrl
                      ? <img src={item.imageUrl} alt="" className="hp-carousel-image" />
                      : <div className="hp-carousel-image hp-qa-image-empty"><FiImage size={14} /></div>}
                    {/* Optional; without it the app draws no caption line at
                        all, so the preview draws none either */}
                    {item.label?.en && <span className="hp-carousel-label">{renderRichText(item.label.en)}</span>}
                  </div>
                );
              })}
            </div>
          </div>
        );
      }

      case 'recentFraw':
        return (
          <div className="hp-section-block">
            {el.title?.en && <div className="hp-section-title">{renderRichText(el.title.en)}</div>}
            <div className="hp-tile-row">
              {Array.from({ length: 4 }).map((_, i) => (
                <div key={i} className="hp-card-placeholder hp-tile-small" />
              ))}
            </div>
          </div>
        );

      // The app draws a Lenses List with the Top Lenses components, so it previews as one
      case 'topExtensions':
      case 'curatedExtensions': {
        // Untitled unless the admin sets a section title (mirrors the app)
        const title = el.title?.en;
        const showRank = el.showRank !== false;
        const count = el.type === 'curatedExtensions' ? (el.extensions?.length ?? 0) : (el.limit ?? 10);
        const images = el.type === 'curatedExtensions' ? el.extensions?.map((ext) => ext.imageUrl) : undefined;
        if (count === 0) {
          return (
            <div className="hp-section-block">
              {title && <div className="hp-section-title">{renderRichText(title)}</div>}
              <div className="hp-qa-empty">Pick lenses</div>
            </div>
          );
        }
        if (el.displayStyle === 'vertical') {
          // Vertical layout: ranking list card
          return (
            <div className="hp-section-block">
              {title && <div className="hp-section-title">{renderRichText(title)}</div>}
              <div className="hp-rankcard">
                {Array.from({ length: Math.min(count, 3) }).map((_, i) => (
                  <div key={i} className="hp-rankrow">
                    {showRank && <span className="hp-rankrow-num">{i + 1}</span>}
                    {images?.[i]
                      ? <img src={images[i]} alt="" className="hp-card-placeholder hp-rankrow-thumb hp-preview-thumb" />
                      : <div className="hp-card-placeholder hp-rankrow-thumb" />}
                    <div className="hp-rankrow-lines">
                      <div className="hp-rankrow-line name" />
                      <div className="hp-rankrow-line creator" />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          );
        }
        // Horizontal layout: ranked strip (numbers optional)
        return (
          <div className="hp-section-block">
            {title && <div className="hp-section-title">{renderRichText(title)}</div>}
            {renderRankedRow(count, showRank, images)}
          </div>
        );
      }

      case 'extensionGrid': {
        const infinite = el.limit == null;
        return (
          <div className="hp-section-block">
            {/* Untitled unless the admin sets a section title (mirrors the app) */}
            {el.title?.en && <div className="hp-section-title">{renderRichText(el.title.en)}</div>}
            <div className="hp-grid-preview">
              <div className="hp-card-placeholder hp-grid-cell">
                <span className="hp-grid-count">{infinite ? '∞' : `× ${el.limit}`}</span>
                <span className="hp-grid-caption">{infinite ? 'infinite scroll' : 'extensions'}</span>
              </div>
            </div>
          </div>
        );
      }
    }
  };

  const renderPreviewElement = (layout: AdminLayout, el: LayoutElement, index: number) => {
    const badges = visibilityBadges(el.visibility);
    const beingEdited = editing?.mode === 'edit' && editing.layoutId === layout.layoutId && editing.draft.elementId === el.elementId;
    const isDragSource = dragging?.kind === 'reorder' && dragging.layoutId === layout.layoutId && dragging.index === index;
    // The reorder hint is a gap index; render the line above the element at
    // that gap (or below the last element for the final gap).
    const hintHere = reorderHint?.layoutId === layout.layoutId ? reorderHint.index : null;
    const lineTop = hintHere !== null && hintHere === index;
    const lineBottom = hintHere !== null && hintHere === layout.elements.length && index === layout.elements.length - 1;
    return (
      <div
        key={el.elementId}
        className={
          `hp-element${beingEdited ? ' selected' : ''}` +
          `${isDragSource ? ' dragging' : ''}` +
          `${lineTop ? ' reorder-line-top' : ''}` +
          `${lineBottom ? ' reorder-line-bottom' : ''}` +
          `${!el.visibility.enabled ? ' disabled' : ''}`
        }
        draggable
        onDragStart={() => handleReorderStart(layout.layoutId, index)}
        onDragOver={(e) => handleReorderOver(e, layout.layoutId, index)}
        onDragEnd={handleReorderEnd}
        onClick={() => openEditModal(layout.layoutId, el)}
      >
        {/* Admin-set section title for element types without a built-in header.
            quickActions is absent on purpose: it renders its own title inline. */}
        {['article', 'banner'].includes(el.type) && el.title?.en && (
          <div className="hp-section-title">{renderRichText(el.title.en)}</div>
        )}
        {renderPreviewContent(el)}

        {(badges.length > 0 || !el.visibility.enabled) && (
          <div className="hp-badges">
            {!el.visibility.enabled && <span className="hp-badge hidden"><FiEyeOff size={10} /> Hidden</span>}
            {badges.map((badge) => (
              <span key={badge} className="hp-badge">{badge}</span>
            ))}
          </div>
        )}

        <div className="hp-toolbar" onClick={(e) => e.stopPropagation()}>
          <span className="hp-tool hp-drag" title="Drag to reorder"><FiMove size={12} /></span>
          <button type="button" className="hp-tool" onClick={() => moveElement(layout.layoutId, index, 'up')} disabled={index === 0}>
            <FiChevronUp size={12} />
          </button>
          <button type="button" className="hp-tool" onClick={() => moveElement(layout.layoutId, index, 'down')} disabled={index === layout.elements.length - 1}>
            <FiChevronDown size={12} />
          </button>
          <button type="button" className="hp-tool danger" onClick={() => removeElement(layout.layoutId, index)}>
            <FiTrash2 size={12} />
          </button>
        </div>
      </div>
    );
  };

  // ---------------------------------------------------------------------------
  // Insert zones: hover "+" between sections, dotted "+" area at the end.
  // During an element-type drag they double as precise drop targets.
  // ---------------------------------------------------------------------------

  /** Element-type menu shown when a "+" is pressed; picking opens the add modal. */
  const renderTypePicker = (layoutId: string, insertAt: number) => (
    <div className="hl-type-picker" onClick={(e) => e.stopPropagation()}>
      {ADD_OPTIONS.map((option) => (
        <button
          key={option.key}
          type="button"
          className="hl-type-picker-item"
          onClick={() => openAddModal(option.create, layoutId, insertAt)}
        >
          {option.icon} {option.label}
        </button>
      ))}
    </div>
  );

  const isPickerAt = (layoutId: string, index: number) =>
    insertPicker?.layoutId === layoutId && insertPicker.index === index;

  const isDropHintAt = (layoutId: string, index: number) =>
    dropHint?.layoutId === layoutId && dropHint.index === index;

  /** Thin hover strip between sections; "+" opens the type picker for that position. */
  const renderInsertZone = (layoutId: string, insertAt: number) => (
    <div
      className={
        `hp-insert${isPickerAt(layoutId, insertAt) ? ' open' : ''}` +
        `${dragging?.kind === 'new' ? ' drop-visible' : ''}` +
        `${isDropHintAt(layoutId, insertAt) ? ' drop-hint' : ''}`
      }
      onDragOver={(e) => {
        if (dragging?.kind === 'new') {
          e.preventDefault();
          e.stopPropagation();
          setDropHint({ layoutId, index: insertAt });
        } else if (dragging?.kind === 'reorder' && dragging.layoutId === layoutId) {
          // Hovering the gap itself while reordering targets this gap
          e.preventDefault();
          e.stopPropagation();
          setReorderGap(layoutId, insertAt);
        }
      }}
      onDragLeave={() => { if (isDropHintAt(layoutId, insertAt)) setDropHint(null); }}
      onDrop={(e) => {
        if (dragging?.kind !== 'new') return;
        e.preventDefault();
        e.stopPropagation();
        handleNewElementDrop(layoutId, insertAt);
      }}
    >
      <button
        type="button"
        className="hp-insert-btn"
        title="Insert element here"
        onClick={(e) => {
          e.stopPropagation();
          setInsertPicker(isPickerAt(layoutId, insertAt) ? null : { layoutId, index: insertAt });
        }}
      >
        <FiPlus size={12} />
      </button>
      {isPickerAt(layoutId, insertAt) && renderTypePicker(layoutId, insertAt)}
    </div>
  );

  /** Always-visible dashed add area at the end of the list. */
  const renderEndZone = (layout: AdminLayout) => {
    const insertAt = layout.elements.length;
    return (
      <div
        className={
          `hp-insert-end${isPickerAt(layout.layoutId, insertAt) ? ' open' : ''}` +
          `${isDropHintAt(layout.layoutId, insertAt) ? ' drop-hint' : ''}`
        }
        onClick={() => setInsertPicker(isPickerAt(layout.layoutId, insertAt) ? null : { layoutId: layout.layoutId, index: insertAt })}
        onDragOver={(e) => {
          if (dragging?.kind === 'reorder' && dragging.layoutId === layout.layoutId) {
            // Reordering onto the end area targets the final gap
            e.preventDefault();
            e.stopPropagation();
            setReorderGap(layout.layoutId, insertAt);
            return;
          }
          if (dragging?.kind !== 'new') return;
          e.preventDefault();
          e.stopPropagation();
          setDropHint({ layoutId: layout.layoutId, index: insertAt });
        }}
        onDragLeave={() => { if (isDropHintAt(layout.layoutId, insertAt)) setDropHint(null); }}
        onDrop={(e) => {
          if (dragging?.kind !== 'new') return;
          e.preventDefault();
          e.stopPropagation();
          handleNewElementDrop(layout.layoutId, insertAt);
        }}
      >
        <FiPlus size={18} />
        <span>{layout.elements.length === 0 ? 'Add your first element' : 'Add element'}</span>
        {isPickerAt(layout.layoutId, insertAt) && renderTypePicker(layout.layoutId, insertAt)}
      </div>
    );
  };

  // ---------------------------------------------------------------------------
  // Phone frame (one per layout)
  // ---------------------------------------------------------------------------

  /**
   * The layout's per-user overrides, opened from the header's user button:
   * chips for who is on it, and a search to add someone. These users see THIS
   * layout wherever they are, so it is the one way a layout reaches anybody
   * without being the active one.
   */
  const renderAssignedUsersModal = () => {
    const layout = layouts.find((l) => l.layoutId === usersModalLayoutId);
    if (!layout) return null;
    const users = layout.assignedUsers || [];
    const searching = userSearchLayoutId === layout.layoutId;
    return (
      <div className="hl-modal-overlay">
        <div className="hl-modal hl-users-modal" onClick={(e) => e.stopPropagation()}>
          <h2 className="hl-modal-title">
            <FiUsers /> Assigned users
            <span className="hl-modal-subtitle"> — {layout.name || 'Untitled layout'}</span>
          </h2>

          <div className="hl-modal-body">
            <small className="hl-hint">
              These users are served this layout instead of the active one, whether or not it is active.
              A user can only be on one layout — assigning them here takes them off any other.
            </small>

            <label className="hl-field-label">
              People on this layout <small className="hl-hint-inline">({users.length})</small>
            </label>
            <div className="hp-user-chips">
              {users.length === 0 && <span className="hp-user-empty">Nobody yet — everyone gets the active layout</span>}
              {users.map((user) => (
                <span key={user.userId} className="hp-user-chip" title={user.userId}>
                  {user.name}
                  <button type="button" title="Remove" onClick={() => unassignUser(layout.layoutId, user.userId)}>
                    <FiX size={10} />
                  </button>
                </span>
              ))}
            </div>

            <label className="hl-field-label">Add someone</label>
            <div className="hp-user-search">
              <input
                type="text"
                className="hl-input"
                placeholder="Search by name, email or user ID…"
                autoFocus
                value={searching ? userQuery : ''}
                onFocus={() => {
                  if (!searching) {
                    setUserSearchLayoutId(layout.layoutId);
                    setUserQuery('');
                    setUserResults([]);
                  }
                }}
                onChange={(e) => {
                  setUserSearchLayoutId(layout.layoutId);
                  setUserQuery(e.target.value);
                }}
              />
              {searching && userSearching && <small className="hp-user-hint">Searching…</small>}
              {searching && !userSearching && userQuery.trim().length >= 2 && userResults.length === 0 && (
                <small className="hp-user-hint">No users found</small>
              )}
              {searching && userResults.length > 0 && (
                <div className="hp-user-results">
                  {userResults.map((user) => (
                    <button
                      key={user.userId}
                      type="button"
                      className="hp-user-result"
                      onClick={() => assignUser(layout.layoutId, { userId: user.userId, name: user.name })}
                    >
                      <span className="hp-user-result-name">{user.name}</span>
                      {user.email && <span className="hp-user-result-sub">{user.email}</span>}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>

          <div className="hl-modal-actions">
            {/* Edits land in the draft as they are made, like every other layout
                change — this only closes the panel; Save writes them all. */}
            <button type="button" className="hl-confirm-btn" onClick={() => setUsersModalLayoutId(null)}>
              Done
            </button>
          </div>
        </div>
      </div>
    );
  };

  const renderPhone = (layout: AdminLayout) => (
    <div key={layout.layoutId} className={`hp-phone-wrap${layout.isActive ? ' active' : ''}`}>
      <div className="hp-layout-header">
        <input
          type="text"
          className="hp-layout-name"
          value={layout.name}
          placeholder="Layout name"
          onChange={(e) => mutateLayout(layout.layoutId, (l) => ({ ...l, name: e.target.value }))}
        />
        <button
          type="button"
          className={`hp-active-toggle${layout.isActive ? ' on' : ''}`}
          title={layout.isActive ? 'This layout is served to the app — click to deactivate' : 'Serve this layout to the app'}
          onClick={() => setActiveLayout(layout.layoutId, !layout.isActive)}
        >
          <FiCheckCircle size={13} /> {layout.isActive ? 'Active' : 'Set Active'}
        </button>
        <button
          type="button"
          className={`hp-tool hp-users-btn${(layout.assignedUserIds || []).length ? ' on' : ''}`}
          title={
            (layout.assignedUserIds || []).length
              ? `${(layout.assignedUserIds || []).length} user(s) served this layout instead of the active one`
              : 'Assign users to this layout'
          }
          onClick={() => {
            // Opens on a clean search, not on whatever was typed last time
            setUsersModalLayoutId(layout.layoutId);
            setUserSearchLayoutId(null);
            setUserQuery('');
            setUserResults([]);
          }}
        >
          <FiUsers size={13} />
          {(layout.assignedUserIds || []).length > 0 && <span>{(layout.assignedUserIds || []).length}</span>}
        </button>
        <button
          type="button"
          className="hp-tool danger hp-layout-delete"
          title="Delete this layout"
          onClick={() => removeLayout(layout.layoutId)}
        >
          <FiTrash2 size={13} />
        </button>
      </div>

      <div className={`hp-phone${layout.isActive ? ' active' : ''}${dragging?.kind === 'new' ? ' drop-ready' : ''}`}>
        {layout.isActive && <div className="hp-active-ribbon">ACTIVE</div>}
        {/* Mock app header: logo + bell */}
        <div className="hp-app-header">
          <span className="hp-logo">FRAW</span>
          <FiBell size={16} className="hp-bell" />
        </div>
        <div
          className="hp-screen"
          onDragOver={(e) => { if (dragging?.kind === 'new') e.preventDefault(); }}
          onDrop={(e) => {
            if (dragging?.kind !== 'new') return;
            e.preventDefault();
            // Dropped on the screen but not on a strip: append at the end
            handleNewElementDrop(layout.layoutId, layout.elements.length);
          }}
        >
          {layout.elements.map((el, index) => (
            <Fragment key={el.elementId}>
              {renderInsertZone(layout.layoutId, index)}
              {renderPreviewElement(layout, el, index)}
            </Fragment>
          ))}
          {renderEndZone(layout)}
        </div>
      </div>
    </div>
  );

  // ---------------------------------------------------------------------------
  // Modal: draft content config + visibility
  // ---------------------------------------------------------------------------

  /** Shared quickActions / carousel editor: one row per tile (image + label + action). */
  const renderTileListEditor = (draft: LayoutElement, field: TilesField, addLabel: string) => {
    const tiles = draft[field] || [];
    return (
      <>
        {tiles.map((tile, index) => {
          const needsTarget = tile.action.type === 'extension' || tile.action.type === 'creator';
          const isExtension = tile.action.type === 'extension';
          const selectedId = isExtension ? tile.action.extensionId : tile.action.userId;
          return (
            <div key={index} className="hl-qa-row">
              <div className="hl-qa-main">
                <label className="hl-qa-thumb" title={tile.imageUrl ? 'Replace image' : 'Upload image'}>
                  {tile.imageUrl
                    ? <img src={tile.imageUrl} alt="" />
                    : <FiImage size={18} />}
                  <input
                    type="file" accept="image/*" hidden
                    onChange={(e) => {
                      const file = e.target.files?.[0];
                      if (file) handleTileImageUpload(field, index, file);
                      e.target.value = '';
                    }}
                  />
                </label>
                <button
                  type="button"
                  className={`hl-input hl-qa-label-btn${tile.label?.en ? '' : ' empty'}`}
                  title={field === 'carouselItems' ? 'Edit caption translations' : 'Edit label translations'}
                  onFocus={() => openLabelEditor({ kind: 'tileLabel', field, index })}
                  onClick={() => openLabelEditor({ kind: 'tileLabel', field, index })}
                >
                  {(tile.label?.en && renderRichText(tile.label.en))
                    || (field === 'carouselItems' ? 'Caption (optional)' : 'Label')}
                </button>
                <select
                  className="hl-input hl-qa-action-select"
                  value={tile.action.type}
                  onChange={(e) => {
                    updateDraftTile(field, index, { action: { type: e.target.value as BannerActionType } });
                    resetTargetSearch();
                  }}
                >
                  {ACTION_OPTIONS.map((option) => (
                    <option key={option.value} value={option.value}>{option.label}</option>
                  ))}
                </select>
                <button type="button" className="hl-target-remove" title="Remove this row" onClick={() => removeDraftTile(field, index)}>
                  <FiTrash2 size={13} />
                </button>
              </div>

              {tile.action.type === 'extensionList' && (
                <select
                  className="hl-input"
                  value={tile.action.categoryId || ''}
                  onChange={(e) => updateDraftTile(field, index, { action: { type: 'extensionList', categoryId: e.target.value || undefined } })}
                >
                  <option value="">Select a category</option>
                  {categories.map((category) => (
                    <option key={category._id} value={category.category_id}>
                      {category.label.en || category.category_id}
                    </option>
                  ))}
                </select>
              )}

              {tile.action.type === 'article' && renderArticleActionSelect(
                tile.action,
                (action) => updateDraftTile(field, index, { action })
              )}

              {tile.action.type === 'url' && (
                <input
                  type="url"
                  className="hl-input"
                  placeholder="https://example.com — opens in the phone's browser"
                  value={tile.action.url || ''}
                  onChange={(e) => updateDraftTile(field, index, { action: { type: 'url', url: e.target.value } })}
                />
              )}

              {needsTarget && (
                selectedId ? (
                  <div className="hl-target-selected">
                    <span className="hl-target-name">{tile.action.targetName || selectedId}</span>
                    <button
                      type="button"
                      className="hl-target-remove"
                      onClick={() => {
                        updateDraftTile(field, index, { action: { type: tile.action.type } });
                        resetTargetSearch();
                      }}
                    >
                      <FiTrash2 size={13} />
                    </button>
                  </div>
                ) : (
                  <div className="hl-target-search">
                    <input
                      type="text"
                      className="hl-input"
                      placeholder={isExtension ? 'Search lenses…' : 'Search users…'}
                      value={qaSearchIndex === index ? targetQuery : ''}
                      onFocus={() => {
                        if (qaSearchIndex !== index) {
                          setQaSearchIndex(index);
                          setTargetQuery('');
                          setTargetResults([]);
                        }
                      }}
                      onChange={(e) => {
                        setQaSearchIndex(index);
                        setTargetQuery(e.target.value);
                      }}
                    />
                    {qaSearchIndex === index && targetSearching && <small className="hl-hint">Searching…</small>}
                    {qaSearchIndex === index && targetResults.length > 0 && (
                      <div className="hl-target-results">
                        {targetResults.map((result) => (
                          <button
                            key={result.id}
                            type="button"
                            className="hl-type-picker-item"
                            onClick={() => {
                              updateDraftTile(field, index, {
                                action: isExtension
                                  ? { type: 'extension', extensionId: result.id, targetName: result.name }
                                  : { type: 'creator', userId: result.id, targetName: result.name },
                              });
                              resetTargetSearch();
                            }}
                          >
                            {result.name}{result.sub ? ` — ${result.sub}` : ''}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                )
              )}
            </div>
          );
        })}
        {tiles.length < MAX_QUICK_ACTIONS && (
          <button type="button" className="hl-qa-add" onClick={() => addDraftTile(field)}>
            <FiPlus size={13} /> {addLabel}
          </button>
        )}
      </>
    );
  };

  /** Layout + rank-number options, shared by Top Lenses and Lenses List. */
  const renderTopLensLookFields = (draft: LayoutElement) => (
    <>
      <label className="hl-field-label">Layout</label>
      <div className="hl-check-row">
        <label className="hl-radio hl-radio-inline">
          <input
            type="radio"
            name="top-extensions-layout"
            checked={draft.displayStyle !== 'vertical'}
            onChange={() => updateDraft({ displayStyle: 'horizontal' })}
          />
          <span>Horizontal strip</span>
        </label>
        <label className="hl-radio hl-radio-inline">
          <input
            type="radio"
            name="top-extensions-layout"
            checked={draft.displayStyle === 'vertical'}
            onChange={() => updateDraft({ displayStyle: 'vertical' })}
          />
          <span>Vertical ranking list</span>
        </label>
      </div>

      <label className="hl-checkbox hl-showrank">
        <input
          type="checkbox"
          checked={draft.showRank !== false}
          onChange={(e) => updateDraft({ showRank: e.target.checked })}
        />
        Show ranking numbers
      </label>
    </>
  );

  /**
   * Moves the picked lens at `from` into gap `gap` (0 = first, length = last).
   * The gaps right above and below the lens itself leave the order unchanged.
   */
  const moveCuratedExtension = (draft: LayoutElement, from: number, gap: number) => {
    const extensions = [...(draft.extensions || [])];
    if (gap < 0 || gap > extensions.length || gap === from || gap === from + 1) return;
    const [moved] = extensions.splice(from, 1);
    extensions.splice(gap > from ? gap - 1 : gap, 0, moved);
    updateDraft({ extensions });
  };

  /** The gap a drag over row `index` points at: its top half = above it, bottom half = below. */
  const lensDropGap = (e: React.DragEvent, index: number, from: number): number | null => {
    const rect = e.currentTarget.getBoundingClientRect();
    const gap = e.clientY < rect.top + rect.height / 2 ? index : index + 1;
    return gap === from || gap === from + 1 ? null : gap;
  };

  const renderConfigEditor = (draft: LayoutElement) => {
    switch (draft.type) {
      case 'article':
        return (
          <>
            <label className="hl-field-label">Article *</label>
            <select
              className="hl-input"
              value={draft.articleId || ''}
              onChange={(e) => updateDraft({ articleId: e.target.value || undefined })}
            >
              <option value="">Select an article</option>
              {articles.map((article) => (
                <option key={article.articleId} value={article.articleId}>
                  {article.title}
                </option>
              ))}
              {/* Keep a saved article selectable even if it no longer appears in the published list */}
              {draft.articleId && !articles.some((a) => a.articleId === draft.articleId) && (
                <option value={draft.articleId}>{draft.article?.title || draft.articleId}</option>
              )}
            </select>
            {(() => {
              const info = articleInfo(draft);
              return draft.articleId && info.imageUrl
                ? <img className="hl-article-preview" src={info.imageUrl} alt="Article cover" />
                : null;
            })()}
          </>
        );

      case 'banner': {
        const banner = draft.banner as BannerConfig;
        return (
          <>
            <label className="hl-field-label">Media *</label>
            {banner.imageUrl || banner.videoUrl ? (
              <div className="hl-media-preview">
                {banner.imageUrl
                  ? <img src={banner.imageUrl} alt="Banner" />
                  : <video src={banner.videoUrl} controls />}
                <button
                  type="button"
                  className="hl-media-remove"
                  onClick={() => updateDraftBanner({
                    imageKey: undefined, imageUrl: undefined,
                    videoKey: undefined, videoUrl: undefined,
                    thumbnailKey: undefined, thumbnailUrl: undefined,
                    hasAudio: undefined,
                  })}
                >
                  <FiTrash2 />
                </button>
              </div>
            ) : (
              <div className="hl-upload-row">
                <label className="hl-upload-label">
                  <FiImage size={20} />
                  <span>Image</span>
                  <input
                    type="file" accept="image/*" hidden
                    onChange={(e) => {
                      const file = e.target.files?.[0];
                      if (file) handleBannerUpload(file, 'image');
                    }}
                  />
                </label>
                <label className="hl-upload-label">
                  <FiVideo size={20} />
                  <span>Video</span>
                  <input
                    type="file" accept="video/*" hidden
                    onChange={(e) => {
                      const file = e.target.files?.[0];
                      if (file) handleBannerUpload(file, 'video');
                    }}
                  />
                </label>
              </div>
            )}
            {uploading && <div className="hl-uploading">Uploading…</div>}

            <label className="hl-field-label">Title (shown on the card, up to two lines)</label>
            <button
              type="button"
              className={`hl-input hl-qa-label-btn hl-title-btn${banner.title?.en ? '' : ' empty'}`}
              title="Edit banner title translations"
              onFocus={() => openLabelEditor({ kind: 'bannerTitle' })}
              onClick={() => openLabelEditor({ kind: 'bannerTitle' })}
            >
              {(banner.title?.en && renderRichText(banner.title.en)) || 'None — click to add'}
            </button>

            <label className="hl-field-label">Subtitle (above the title, one line)</label>
            <button
              type="button"
              className={`hl-input hl-qa-label-btn hl-title-btn${banner.subtitle?.en ? '' : ' empty'}`}
              title="Edit banner subtitle translations"
              onFocus={() => openLabelEditor({ kind: 'bannerSubtitle' })}
              onClick={() => openLabelEditor({ kind: 'bannerSubtitle' })}
            >
              {(banner.subtitle?.en && renderRichText(banner.subtitle.en)) || 'None — click to add'}
            </button>

            <label className="hl-field-label">Button (optional)</label>
            <button
              type="button"
              className={`hl-input hl-qa-label-btn hl-title-btn${banner.buttonLabel?.en ? '' : ' empty'}`}
              title="Edit banner button translations"
              onFocus={() => openLabelEditor({ kind: 'bannerButton' })}
              onClick={() => openLabelEditor({ kind: 'bannerButton' })}
            >
              {banner.buttonLabel?.en || 'None — the whole card runs the action'}
            </button>
            <small className="hl-hint">
              {banner.buttonLabel?.en
                ? 'A button sits in the bottom corner and is the only thing that runs the action — tapping the card itself does nothing.'
                : 'Add a button to make the card behave like the intro card: the button runs the action, and the card itself stops being tappable.'}
            </small>

            <label className="hl-field-label">Behind the text</label>
            <div className="hl-segmented" role="radiogroup" aria-label="Behind the text">
              {BANNER_BACKDROP_SEGMENTS.map((segment) => (
                <button
                  key={segment.value}
                  type="button"
                  role="radio"
                  aria-checked={(banner.backdrop || 'gradient') === segment.value}
                  className={`hl-segment${(banner.backdrop || 'gradient') === segment.value ? ' active' : ''}`}
                  onClick={() => updateDraftBanner({ backdrop: segment.value })}
                >
                  {segment.label}
                </button>
              ))}
            </div>
            <small className="hl-hint">
              {BANNER_BACKDROP_SEGMENTS.find((s) => s.value === (banner.backdrop || 'gradient'))?.hint}
              {' '}A banner with no title, subtitle or button draws nothing either way.
            </small>

            {/* Only the frosted band draws a tint, so the control appears with it. The
                value is kept when the admin switches backdrop, and comes back with it. */}
            {(banner.backdrop || 'gradient') === 'blur' && (
              <>
                <label className="hl-field-label">Band tint</label>
                {banner.backdropColor ? (
                  <div className="hl-tint-row">
                    <input
                      type="color"
                      className="hl-tint-input"
                      aria-label="Band tint colour"
                      value={banner.backdropColor}
                      onChange={(e) => updateDraftBanner({ backdropColor: e.target.value })}
                    />
                    <code className="hl-tint-value">{banner.backdropColor}</code>
                    <button
                      type="button"
                      className="hl-tint-clear"
                      onClick={() => updateDraftBanner({ backdropColor: undefined })}
                    >
                      Clear
                    </button>
                  </div>
                ) : (
                  <button
                    type="button"
                    className="hl-tint-add"
                    onClick={() => updateDraftBanner({ backdropColor: DEFAULT_BANNER_TINT })}
                  >
                    Add a tint
                  </button>
                )}
                <small className="hl-hint">
                  A colour under the frosted band, drawn at half opacity — it shifts the tone of
                  that strip of the artwork rather than covering it. Leave it off for the plain frost.
                </small>
              </>
            )}

            <label className="hl-field-label">Tap action *</label>
            {/* A dropdown rather than a radio per action: the list is thirteen long and
                pushed everything below it off the panel. Same control the quickActions
                and carousel rows use, so the one description that matters — the
                selected action's — moves to the hint below. */}
            <select
              className="hl-input"
              value={banner.action.type}
              onChange={(e) => {
                updateDraftBanner({ action: { type: e.target.value as BannerActionType } });
                resetTargetSearch();
              }}
            >
              {ACTION_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>{option.label}</option>
              ))}
            </select>
            <small className="hl-hint">
              {ACTION_OPTIONS.find((option) => option.value === banner.action.type)?.description}
            </small>

            {banner.action.type === 'extensionList' && (
              <>
                <label className="hl-field-label">Category *</label>
                <select
                  className="hl-input"
                  value={banner.action.categoryId || ''}
                  onChange={(e) => updateDraftBanner({ action: { type: 'extensionList', categoryId: e.target.value || undefined } })}
                >
                  <option value="">Select a category</option>
                  {categories.map((category) => (
                    <option key={category._id} value={category.category_id}>
                      {category.label.en || category.category_id}
                    </option>
                  ))}
                </select>
              </>
            )}

            {banner.action.type === 'article' && (
              <>
                <label className="hl-field-label">Article *</label>
                {renderArticleActionSelect(banner.action, (action) => updateDraftBanner({ action }))}
              </>
            )}

            {banner.action.type === 'url' && (
              <>
                <label className="hl-field-label">Website URL *</label>
                <input
                  type="url"
                  className="hl-input"
                  placeholder="https://example.com"
                  value={banner.action.url || ''}
                  onChange={(e) => updateDraftBanner({ action: { type: 'url', url: e.target.value } })}
                />
                <small className="hl-hint">Opens in the phone's default browser, outside the app. Must start with http:// or https://.</small>
              </>
            )}

            {(banner.action.type === 'extension' || banner.action.type === 'creator') && (() => {
              const isExtension = banner.action.type === 'extension';
              const selectedId = isExtension ? banner.action.extensionId : banner.action.userId;
              return (
                <>
                  <label className="hl-field-label">{isExtension ? 'Extension *' : 'User *'}</label>
                  {selectedId ? (
                    <div className="hl-target-selected">
                      <span className="hl-target-name">{banner.action.targetName || selectedId}</span>
                      <button
                        type="button"
                        className="hl-target-remove"
                        onClick={() => {
                          updateDraftBanner({ action: { type: banner.action.type } });
                          resetTargetSearch();
                        }}
                      >
                        <FiTrash2 size={13} />
                      </button>
                    </div>
                  ) : (
                    <div className="hl-target-search">
                      <input
                        type="text"
                        className="hl-input"
                        placeholder={isExtension ? 'Search lenses…' : 'Search users…'}
                        value={targetQuery}
                        onChange={(e) => setTargetQuery(e.target.value)}
                      />
                      {targetSearching && <small className="hl-hint">Searching…</small>}
                      {targetResults.length > 0 && (
                        <div className="hl-target-results">
                          {targetResults.map((result) => (
                            <button
                              key={result.id}
                              type="button"
                              className="hl-type-picker-item"
                              onClick={() => {
                                updateDraftBanner({
                                  action: isExtension
                                    ? { type: 'extension', extensionId: result.id, targetName: result.name }
                                    : { type: 'creator', userId: result.id, targetName: result.name },
                                });
                                resetTargetSearch();
                              }}
                            >
                              {result.name}{result.sub ? ` — ${result.sub}` : ''}
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                </>
              );
            })()}
          </>
        );
      }

      case 'quickActions':
        return (
          <>
            <label className="hl-field-label">Description (optional)</label>
            <button
              type="button"
              className={`hl-input hl-qa-label-btn hl-title-btn${draft.description?.en ? '' : ' empty'}`}
              title="Edit description translations"
              onFocus={() => openLabelEditor({ kind: 'elementDescription' })}
              onClick={() => openLabelEditor({ kind: 'elementDescription' })}
            >
              {draft.description?.en || 'None — supporting text under the title'}
            </button>
            <small className="hl-hint">With a section title and/or a description, the row uses the <strong>titled</strong> layout: the text sits on the left and stays put while the tiles move to the right — at most 2 visible, the rest reached by scrolling. Scrolling raises a solid dark backdrop behind the tiles — the text disappears behind it so the tiles stay fully readable as they travel across, dissolving only at the left edge. The title is shown beside the tiles instead of as a header above them. Leave both empty for the plain full-width row of tiles.</small>

            <label className="hl-field-label">Actions * <small className="hl-hint-inline">(max {MAX_QUICK_ACTIONS})</small></label>
            {renderTileListEditor(draft, 'quickActions', 'Add action')}
            <small className="hl-hint">Each action renders as a square tile with its label underneath. Click a label to edit its translations — at most {MAX_QUICK_ACTION_LABEL} characters per language.</small>
          </>
        );

      case 'carousel':
        return (
          <>
            <label className="hl-field-label">Items * <small className="hl-hint-inline">(max {MAX_QUICK_ACTIONS})</small></label>
            {renderTileListEditor(draft, 'carouselItems', 'Add item')}

            <div className="hl-toggle-group">
              {/* Wide mode does nothing at any other count, so it is only offered
                  at the one it applies to. A value set here survives adding or
                  removing items — it just goes back into effect at two again. */}
              {(draft.carouselItems || []).length === 2 && (
                <label className="hl-checkbox">
                  <input
                    type="checkbox"
                    checked={!!draft.wideMode}
                    onChange={(e) => updateDraft({ wideMode: e.target.checked })}
                  />
                  Wide mode
                </label>
              )}

              <label className="hl-checkbox">
                <input
                  type="checkbox"
                  checked={draft.infiniteScroll !== false}
                  onChange={(e) => updateDraft({ infiniteScroll: e.target.checked })}
                />
                Infinite scroll
              </label>
            </div>
          </>
        );

      case 'curatedExtensions': {
        const picked = draft.extensions || [];
        const full = picked.length >= MAX_CURATED_EXTENSIONS;
        return (
          <>
            <label className="hl-field-label">
              Lenses * <small className="hl-hint-inline">({picked.length}/{MAX_CURATED_EXTENSIONS}, shown in this order. Drag to reorder)</small>
            </label>
            {/* Drops land on the list, not a row, so letting go in the gap between
                two rows still uses the gap the last row hovered pointed at */}
            <div
              className="hl-curated-list"
              onDragOver={(e) => { if (lensDrag) e.preventDefault(); }}
              onDrop={(e) => {
                if (!lensDrag) return;
                e.preventDefault();
                if (lensDrag.gap != null) moveCuratedExtension(draft, lensDrag.from, lensDrag.gap);
                setLensDrag(null);
              }}
            >
              {picked.map((ext, index) => (
                <div
                  key={ext.id}
                  className={
                    'hl-target-selected hl-curated-row' +
                    (lensDrag?.from === index ? ' dragging' : '') +
                    (lensDrag?.gap === index ? ' drop-before' : '') +
                    (lensDrag?.gap === picked.length && index === picked.length - 1 ? ' drop-after' : '')
                  }
                  draggable
                  onDragStart={(e) => {
                    e.dataTransfer.effectAllowed = 'move';
                    // Firefox starts no drag without data
                    e.dataTransfer.setData('text/plain', ext.id);
                    setLensDrag({ from: index, gap: null });
                  }}
                  onDragOver={(e) => {
                    if (!lensDrag) return;
                    e.preventDefault();
                    e.dataTransfer.dropEffect = 'move';
                    const gap = lensDropGap(e, index, lensDrag.from);
                    if (gap !== lensDrag.gap) setLensDrag({ ...lensDrag, gap });
                  }}
                  onDragEnd={() => setLensDrag(null)}
                >
                  <span
                    className="hl-curated-handle"
                    role="button"
                    tabIndex={0}
                    title="Drag to reorder (or focus and use ↑ / ↓)"
                    aria-label={`Reorder ${ext.name}, position ${index + 1} of ${picked.length}`}
                    onKeyDown={(e) => {
                      if (e.key === 'ArrowUp') {
                        e.preventDefault();
                        moveCuratedExtension(draft, index, index - 1);
                      } else if (e.key === 'ArrowDown') {
                        e.preventDefault();
                        moveCuratedExtension(draft, index, index + 2);
                      }
                    }}
                  >
                    <FiMove size={13} />
                  </span>
                  <span className="hl-curated-rank">{index + 1}</span>
                  {ext.imageUrl
                    ? <img src={ext.imageUrl} alt="" className="hl-curated-thumb" draggable={false} />
                    : <span className="hl-curated-thumb hl-curated-thumb-empty"><FiImage size={14} /></span>}
                  <span className="hl-target-name">{ext.name}</span>
                  {ext.listed === false && (
                    <span className="hl-curated-hidden" title="Private, unpublished, archived, deleted or suspended: the app skips it">
                      <FiEyeOff size={12} /> hidden
                    </span>
                  )}
                  <button
                    type="button"
                    className="hl-target-remove"
                    title="Remove"
                    onClick={() => updateDraft({ extensions: picked.filter((p) => p.id !== ext.id) })}
                  >
                    <FiTrash2 size={13} />
                  </button>
                </div>
              ))}
            </div>
            <button
              type="button"
              className="hl-qa-add"
              disabled={full}
              title={full ? `The list holds at most ${MAX_CURATED_EXTENSIONS} lenses` : undefined}
              onClick={() => setLensPickerOpen(true)}
            >
              <FiPlus size={14} /> Add lenses
            </button>
            <LensPickerModal
              open={lensPickerOpen}
              pickedIds={picked.map((ext) => ext.id)}
              remaining={MAX_CURATED_EXTENSIONS - picked.length}
              onAdd={(lenses) => {
                updateDraft({ extensions: [...picked, ...lenses] });
                setLensPickerOpen(false);
              }}
              onClose={() => setLensPickerOpen(false)}
            />
            <small className="hl-hint">
              The app shows these lenses in this order, with no ranking. A lens that isn't publicly listed when the app
              loads the home screen is skipped: private, unpublished, archived, deleted or suspended, or its creator isn't
              on a creator plan (official lenses are exempt). If every lens is skipped, the section is hidden. App builds
              older than the one that added this section don't show it at all.
            </small>

            {renderTopLensLookFields(draft)}
          </>
        );
      }

      case 'topExtensions': {
        const isCategory = !!draft.categoryId;
        return (
          <>
            <label className="hl-field-label">Category</label>
            <select
              className="hl-input"
              value={draft.categoryId || ''}
              onChange={(e) => updateDraft({ categoryId: e.target.value || undefined })}
            >
              <option value="">All categories (global trending)</option>
              {categories.map((category) => (
                <option key={category._id} value={category.category_id}>
                  {category.label.en || category.category_id}
                </option>
              ))}
            </select>
            <small className="hl-hint">
              {isCategory
                ? 'Ranked by trending within the selected category.'
                : 'Ranked by global trending (usage + likes).'}
            </small>

            {renderTopLensLookFields(draft)}

            <label className="hl-field-label">How many</label>
            <input
              type="number"
              className="hl-input hl-input-number"
              min={1}
              max={50}
              value={draft.limit ?? 10}
              onChange={(e) => updateDraft({ limit: parseInt(e.target.value) || 10 })}
            />
          </>
        );
      }

      case 'extensionGrid': {
        const infinite = draft.limit == null;
        return (
          <>
            <label className="hl-checkbox">
              <input
                type="checkbox"
                checked={infinite}
                onChange={(e) => updateDraft({ limit: e.target.checked ? null : 20 })}
              />
              Infinite scroll (must be the last element)
            </label>
            {!infinite && (
              <>
                <label className="hl-field-label">How many</label>
                <input
                  type="number"
                  className="hl-input hl-input-number"
                  min={1}
                  max={100}
                  value={draft.limit ?? 20}
                  onChange={(e) => updateDraft({ limit: parseInt(e.target.value) || 20 })}
                />
              </>
            )}
          </>
        );
      }

      case 'recentFraw':
        return <small className="hl-hint">The user's recently used lenses. Hidden automatically when the list is empty. No extra configuration.</small>;
    }
  };

  const renderVisibilityEditor = (draft: LayoutElement) => {
    const v = draft.visibility;
    return (
      <div className="hl-visibility-panel">
        <label className="hl-checkbox">
          <input
            type="checkbox"
            checked={v.enabled}
            onChange={(e) => updateDraftVisibility({ enabled: e.target.checked })}
          />
          Enabled
        </label>

        <label className="hl-field-label">Who sees it</label>
        <div className="hl-segmented" role="radiogroup" aria-label="Who sees it">
          {LOGIN_STATE_SEGMENTS.map((segment) => (
            <button
              key={segment.value}
              type="button"
              role="radio"
              aria-checked={v.loginState === segment.value}
              className={`hl-segment${v.loginState === segment.value ? ' active' : ''}`}
              onClick={() => updateDraftVisibility({ loginState: segment.value })}
            >
              {segment.label}
            </button>
          ))}
        </div>

        <label className="hl-field-label">Plans <small className="hl-hint-inline">(none = all)</small></label>
        <div className="hl-check-row">
          {PLANS.map((plan) => (
            <label key={plan} className="hl-checkbox hl-checkbox-inline">
              <input
                type="checkbox"
                checked={v.plans.includes(plan)}
                onChange={(e) => {
                  const plans = e.target.checked ? [...v.plans, plan] : v.plans.filter((p) => p !== plan);
                  updateDraftVisibility({ plans });
                }}
              />
              {plan}
            </label>
          ))}
        </div>

        <label className="hl-field-label">Platforms <small className="hl-hint-inline">(none = both)</small></label>
        <div className="hl-check-row">
          {PLATFORMS.map((platform) => (
            <label key={platform.id} className="hl-checkbox hl-checkbox-inline">
              <input
                type="checkbox"
                checked={v.platforms.includes(platform.id)}
                onChange={(e) => {
                  const platforms = e.target.checked
                    ? [...v.platforms, platform.id]
                    : v.platforms.filter((p) => p !== platform.id);
                  updateDraftVisibility({ platforms });
                }}
              />
              {platform.label}
            </label>
          ))}
        </div>
      </div>
    );
  };

  /**
   * Localized-text translations modal: opened from a tile row's label field,
   * an element's section title field, or the banner title field — layered
   * above the element modal. Edits write straight into the draft, so Done just
   * closes and Cancel writes the snapshot taken at open time back over them
   * (the element modal's Cancel still discards everything).
   */
  const renderLabelModal = () => {
    if (!labelEditor || !editing) return null;
    const draft = editing.draft;
    // A quickActions tile is the only text here that must always carry English.
    // Everything else — carousel labels included — is "required only if any
    // translation is set", so the * and the placeholder follow this one flag.
    const englishAlwaysRequired = labelEditor.kind === 'tileLabel' && labelEditor.field === 'quickActions';

    let heading: string;
    let maxLength: number;
    let value: LocalizedLabel;
    let requiredHint: string;
    let onTextChange: (code: string, text: string) => void;
    // Writes a whole localized map back — how Cancel restores the snapshot,
    // including restoring "never set" (undefined) rather than an empty map.
    let onReplace: (next: LocalizedLabel | undefined) => void;

    if (labelEditor.kind === 'tileLabel') {
      const { field, index } = labelEditor;
      const tile = (draft[field] || [])[index];
      if (!tile) return null;
      const isCaption = field === 'carouselItems';
      heading = isCaption ? 'Caption translations' : 'Label translations';
      maxLength = isCaption ? MAX_CAROUSEL_CAPTION : MAX_QUICK_ACTION_LABEL;
      value = tile.label || { en: '' };
      requiredHint = isCaption
        ? `Leave everything empty for a card with no caption. If any translation is set, English is required — the app falls back to it. At most ${MAX_CAROUSEL_CAPTION} characters each. Select part of a caption to bold, italicize, underline or color it — the field shows it the way the app draws it.`
        : `English is required — the app falls back to it when a translation is missing. Each label can be at most ${MAX_QUICK_ACTION_LABEL} characters.`;
      onTextChange = (code, text) => updateDraftTile(field, index, { label: { ...(tile.label || { en: '' }), [code]: text } });
      onReplace = (next) => updateDraftTile(field, index, { label: next });
    } else if (labelEditor.kind === 'elementDescription') {
      heading = 'Description translations';
      maxLength = MAX_ELEMENT_DESCRIPTION;
      value = draft.description || { en: '' };
      requiredHint = `Leave everything empty for no description. If any translation is set, English is required — the app falls back to it. At most ${MAX_ELEMENT_DESCRIPTION} characters each.`;
      onTextChange = (code, text) => updateDraft({ description: { ...(draft.description || { en: '' }), [code]: text } });
      onReplace = (next) => updateDraft({ description: next });
    } else if (labelEditor.kind === 'bannerTitle') {
      const banner = draft.banner;
      if (!banner) return null;
      heading = 'Banner title translations';
      maxLength = MAX_TITLE_LENGTH;
      value = banner.title || { en: '' };
      requiredHint = `Leave everything empty for no title. If any translation is set, English is required — the app falls back to it. The card gives it up to two lines, at most ${MAX_TITLE_LENGTH} characters each. Select part of a title to bold, italicize, underline or color it — the field shows it the way the app draws it.`;
      onTextChange = (code, text) => updateDraftBanner({ title: { ...(banner.title || { en: '' }), [code]: text } });
      onReplace = (next) => updateDraftBanner({ title: next });
    } else if (labelEditor.kind === 'bannerSubtitle') {
      const banner = draft.banner;
      if (!banner) return null;
      heading = 'Banner subtitle translations';
      maxLength = MAX_BANNER_SUBTITLE;
      value = banner.subtitle || { en: '' };
      requiredHint = `Leave everything empty for no subtitle. If any translation is set, English is required — the app falls back to it. The card gives it one line above the title, at most ${MAX_BANNER_SUBTITLE} characters each. Select part of it to bold, italicize, underline or color it — the field shows it the way the app draws it.`;
      onTextChange = (code, text) => updateDraftBanner({ subtitle: { ...(banner.subtitle || { en: '' }), [code]: text } });
      onReplace = (next) => updateDraftBanner({ subtitle: next });
    } else if (labelEditor.kind === 'bannerButton') {
      const banner = draft.banner;
      if (!banner) return null;
      heading = 'Banner button translations';
      maxLength = MAX_BANNER_BUTTON_LABEL;
      value = banner.buttonLabel || { en: '' };
      requiredHint = `Leave everything empty for a card with no button — the whole card runs the action instead. If any translation is set, English is required — the app falls back to it. At most ${MAX_BANNER_BUTTON_LABEL} characters each.`;
      onTextChange = (code, text) => updateDraftBanner({ buttonLabel: { ...(banner.buttonLabel || { en: '' }), [code]: text } });
      onReplace = (next) => updateDraftBanner({ buttonLabel: next });
    } else {
      heading = 'Section title translations';
      maxLength = MAX_TITLE_LENGTH;
      value = draft.title || { en: '' };
      requiredHint = `Leave everything empty for no custom title. If any translation is set, English is required — the app falls back to it. At most ${MAX_TITLE_LENGTH} characters each. Select part of a title to bold, italicize, underline or color it — the field shows it the way the app draws it.`;
      onTextChange = (code, text) => updateDraft({ title: { ...(draft.title || { en: '' }), [code]: text } });
      onReplace = (next) => updateDraft({ title: next });
    }

    // The texts the app parses tags out of: a section title, a banner's card title
    // and subtitle, and a carousel card's caption. Everything else here is plain —
    // a button's face included, since it is a control, not a line of copy.
    const formatted =
      labelEditor.kind === 'elementTitle'
      || labelEditor.kind === 'bannerTitle'
      || labelEditor.kind === 'bannerSubtitle'
      || (labelEditor.kind === 'tileLabel' && labelEditor.field === 'carouselItems');

    const placeholderFor = (required?: boolean) =>
      required
        ? (englishAlwaysRequired ? 'Required' : 'Required if any translation is set')
        : 'Falls back to English';

    const cancelLabelEditor = () => {
      onReplace(labelSnapshot.current);
      setLabelEditor(null);
    };
    cancelLabelEditorRef.current = cancelLabelEditor;

    return (
      <div className="hl-modal-overlay hl-label-overlay">
        <div
          className={`hl-modal hl-label-modal${formatted ? ' wide' : ''}`}
          onClick={(e) => e.stopPropagation()}
        >
          <h2 className="hl-modal-title">{heading}</h2>
          {/* Same padded, scrollable body the element modal uses */}
          <div className="hl-modal-body">
            <table className="hl-label-table">
              <thead>
                <tr>
                  <th>Language</th>
                  <th>Text</th>
                </tr>
              </thead>
              <tbody>
                {LABEL_LANGUAGES.map((lang) => (
                  <tr key={lang.code}>
                    <td className="hl-label-lang">{lang.name}{lang.required && englishAlwaysRequired ? ' *' : ''}</td>
                    <td>
                      {formatted ? (
                        <>
                          <RichTextField
                            value={value[lang.code] || ''}
                            onChange={(markup) => onTextChange(lang.code, markup)}
                            placeholder={placeholderFor(lang.required)}
                            colors={TITLE_COLORS}
                            ariaLabel={`${lang.name} ${heading.replace(' translations', '').toLowerCase()}`}
                          />
                          {/* The cap counts what a user reads, so the count has
                              to be shown — the tags are invisible in the field */}
                          <div className={`hl-rte-count${visibleLength(value[lang.code] || '') > maxLength ? ' over' : ''}`}>
                            {visibleLength(value[lang.code] || '')}/{maxLength}
                          </div>
                        </>
                      ) : (
                        <input
                          type="text"
                          className="hl-input"
                          maxLength={maxLength}
                          placeholder={placeholderFor(lang.required)}
                          value={value[lang.code] || ''}
                          onChange={(e) => onTextChange(lang.code, e.target.value)}
                        />
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <small className="hl-hint">{requiredHint}</small>
          </div>
          <div className="hl-modal-actions">
            <button type="button" className="hl-cancel-btn" onClick={cancelLabelEditor}>
              Cancel
            </button>
            <button type="button" className="hl-confirm-btn" onClick={() => setLabelEditor(null)}>
              Done
            </button>
          </div>
        </div>
      </div>
    );
  };

  const renderModal = () => {
    if (!editing) return null;
    const { mode, draft, layoutId } = editing;
    const layoutName = layouts.find((l) => l.layoutId === layoutId)?.name;
    return (
      <div className="hl-modal-overlay">
        <div className="hl-modal" onClick={(e) => e.stopPropagation()}>
          <h2 className="hl-modal-title">
            {mode === 'add' ? 'Add' : 'Edit'} {elementLabel(draft)}
            {layoutName && <span className="hl-modal-subtitle"> — {layoutName}</span>}
          </h2>

          <div className="hl-modal-body">
            <div className="hl-modal-section">
              <label className="hl-field-label">Section title (optional)</label>
              <button
                type="button"
                className={`hl-input hl-qa-label-btn hl-title-btn${draft.title?.en ? '' : ' empty'}`}
                title="Edit section title translations"
                onFocus={() => openLabelEditor({ kind: 'elementTitle' })}
                onClick={() => openLabelEditor({ kind: 'elementTitle' })}
              >
                {(draft.title?.en && renderRichText(draft.title.en))
                  || (draft.type === 'quickActions'
                    ? 'None — shown beside the tiles when set'
                    : 'None — shown above the section when set')}
              </button>

              {renderConfigEditor(draft)}
            </div>

            <label className="hl-panel-title hl-panel-subtitle">Visibility</label>
            {renderVisibilityEditor(draft)}
          </div>

          <div className="hl-modal-actions">
            <button type="button" className="hl-cancel-btn" onClick={cancelModal} disabled={uploading}>
              Cancel
            </button>
            <button type="button" className="hl-confirm-btn" onClick={confirmModal} disabled={uploading}>
              {mode === 'add' ? 'Add to layout' : 'Apply'}
            </button>
          </div>
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="page home-layout-page">
        <div className="page-header"><h1><FiLayout /> Home Layout</h1></div>
        <div className="hl-empty">Loading…</div>
      </div>
    );
  }

  return (
    <div className="page home-layout-page">
      <div className="page-header">
        <h1><FiLayout /> Home Layout</h1>
        <div className="hl-header-actions">
          {savedAt && <span className="hl-saved-hint">Saved at {savedAt}</span>}
          {dirty && <span className="hl-dirty-hint">Unsaved changes</span>}
          <button className="hl-add-layout-btn" onClick={addLayout}>
            <FiPlus /> Add Layout
          </button>
          <button className="hl-save-btn" onClick={handleSave} disabled={saving || !dirty}>
            <FiSave /> {saving ? 'Saving…' : 'Save Layouts'}
          </button>
        </div>
      </div>

      {error && <div className="hl-error">{error}</div>}

      {/* Transparent overlay to close the type picker on outside click */}
      {insertPicker !== null && <div className="hl-picker-overlay" onClick={() => setInsertPicker(null)} />}

      <div className="hl-workspace">
        {/* Left: draggable element list */}
        <div className="hl-side">
          <label className="hl-side-title"><FiMove /> Elements</label>
          {ADD_OPTIONS.map((option) => (
            <button
              key={option.key}
              type="button"
              className="hl-add-btn hl-draggable"
              title="Drag onto a layout"
              draggable
              onDragStart={(e) => {
                e.dataTransfer.setData('text/plain', option.key);
                e.dataTransfer.effectAllowed = 'copy';
                setDragging({ kind: 'new', typeKey: option.key });
              }}
              onDragEnd={() => {
                setDragging(null);
                setDropHint(null);
              }}
            >
              {option.icon} {option.label}
            </button>
          ))}
          <small className="hl-side-hint">Drag an element onto a phone to add it.</small>
        </div>

        {/* Phones, horizontally */}
        <div className="hl-phones">
          {layouts.map(renderPhone)}
          {layouts.length === 0 && (
            <div className="hl-no-layouts">
              No layouts yet — the app uses its built-in home screen.
              <br />
              <button className="hl-add-layout-btn" onClick={addLayout}>
                <FiPlus /> Add Layout
              </button>
            </div>
          )}
        </div>
      </div>

      {renderModal()}
      {renderLabelModal()}
      {renderAssignedUsersModal()}
      {renderLeavePrompt()}
    </div>
  );
}
